// app-context.js
import React, { createContext, useContext, useEffect } from "react";
import { useImmerReducer } from "use-immer";
import firebase, { db, analytics, batch } from "./firebase.js";
import { doc, collection, setDoc, addDoc, deleteDoc, getDoc, updateDoc, getDocs, query, where, onSnapshot, serverTimestamp, writeBatch, increment } from "firebase/firestore";
import { logEvent } from "firebase/analytics";
import { AuthContext } from "./auth-provider.js";
import { matchPath, useLocation, useSearchParams } from "react-router-dom";
import { nanoid } from "nanoid";
import { v4 as uuidv4 } from 'uuid';
import assets from "./assets.js";
import { audioUpload } from "../helpers/index.js";
import { emptyStory, getAudioUrl, getMediaUrl } from "./utility.js";
import hostConfig from "./config.js";
import { create } from "@mui/material/styles/createTransitions.js";
import { CatchingPokemonSharp } from "@mui/icons-material";
import { use } from "react";
import { current } from "immer";

// import { storyConstructor, albumConstructor } from "../utils/data"

const userAgent = typeof window.navigator === "undefined" ? "" : navigator.userAgent;
const isMobile = Boolean(userAgent.match(/Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i))
const isMobileSize = () => { return Boolean(userAgent.match(/Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i) || (window && window.innerWidth <= 820)); }

// Initial app context state
// TODO: load from the database
const initialStory = {
    parts: [{
        title: "Sample", order: ["id-4NNPGRgIT6CbzCXLNHerL",
            "id-4NNPGRgIT6CbzCXLNHerM", "id-4NNPGRgIT6CbzCXLNHerN"]
    }],
    title: "Sample Storyboard",
    target_duration: 15,
    pace: 1.5,
    portal_id: nanoid(12),
    num_shots: null,
    created: null,
    deleted_at: null,
    timerStart: null,
    isCompetition: false,
    last_audio: null,
    audio: null,
    last_cut: null,
    is_vertical: false,
    storyStyle: null,
    shots: [{
        header: "Goal",
        description: 'HERO wants X',
        meta: 'Establish character & context',
        image: "https://storage.googleapis.com/v333m-clip/tgt/tour10/th/thmb009a.jpg",
        id: "id-4NNPGRgIT6CbzCXLNHerL",
        deleted_at: null
    },
    {
        header: 'Challenge',
        description: 'Faces OBSTACLE',
        meta: 'Emotional arc before & after obstacle',
        image: "https://storage.googleapis.com/v333m-clip/tgt/tour10/th/thmb004a.jpg",
        id: "id-4NNPGRgIT6CbzCXLNHerM",
        deleted_at: null
    },
    {
        header: "Outcome",
        description: 'This is what happened',
        meta: 'Reveal uncertainty',
        image: "https://storage.googleapis.com/v333m-clip/tgt/tour10/th/thmb001a.jpg",
        id: "id-4NNPGRgIT6CbzCXLNHerN",
        deleted_at: null
    }]
}

const initialState = {
    story: initialStory,
    app: {
        isLoading: true,
        isMobile,
        isMobileSize: isMobileSize(),
        alerts: {},
        modal: {},
        upload: null,
        importing: null,
        storyImport: null,
        userIsStoryOwner: false,
        prioritizeLivePreview: true,
        previewVolume: 1,
        musicVolume: 1,
        forkingStory: null,
        initAndFork: null,
        tmpRef: null,
        updateUserState: null,
        savingCut: null,
        migratingAudio: null,
        updateCredits: null,
        toggleStoryStar: null,
        createVeneer: null,
        movingTake: false,
        portal: {
            id: null,
            requested_id: null,
            requirePass: null,
        },
        custom: {
            is_hunt: false,
            gameDuration: 12 * 60 * 60,
        },
        loadingPortalId: null,
        contributedCount: 0,
        controls: {
            backSelectOpen: false,
            addFabOpen: false,
            flip: false,
            drawerIsOpen: false,
            storiesOpen: false,
            sharedStorysOpen: false,
            inspirationOpen: false,
        },
        cardPreviews: {},
        cardPrice: 2,
        settings: {
            promptsVisible: true,                   // impacts app & stored in firebase, can be set on a settings page. 
            savedPreviewHasAudio: true,
            proxyIsBlackAndWhite: false,
            ytImportIsBlackAndWhite: false,
            userCanUploadLargeFiles: true,
            userCanListenToTakes: true,
            videoImportIncludesAudio: false,
            communityCanVeneer: true,               // impacts app & stored in firebase, an be set on a settings page. 
            communityCanUpload: true,
            communityCanCamera: true,
            communityCanToggleLivePreview: false,   // impacts app & stored in firebase, an be set on a settings page. 
            communityCanViewSavedPreview: true,     // This lets community see high res color (and thus could download it)
            communityCanDownloadTakes: true,
            communityCanDownloadUploadedAssets: false,
            communityCanDeleteTakes: false,         // this does allow community members to remove from firebase for view, but does not delete files in the backend
            communityCanListenToAudio: true,        //        
            communityCanForkWithMedia: true,
            communityCanShare: true,
            communityCanStarred: true,
            communityCanChangeStoryStyle: false,

            // communityCanSelectTakes: true,          // tested and never rolled out to prod: we disabled suppport of state.app.settings.communityCanSelectTakes - its effectively false till we manage the data model and can confirm on dev
            // communityShotDescriptionEdit: true,  // an idea:  not used yet
            // communityCanDownload: false,         // an idea:  not used yet --  pershot, this has deeper firebase permission settings
        },
        newSettings: false,
    },
    userData: {},
    stories: {
        storyList: null,
        sharedStoryList: [],
        activeStoryRef: null,
        nextStoryMetadata: null,
        activeStory: {},
        activeShotList: [],
        activeTakeList: [],
        activeRefList: [],
        activeVeneerLUT: {},
        albumSourceSet: new Set(),
        starredStories: [],
        generateStory: null,
        lastActiveStory: {},
        setLastActive: null,
    },
    generate: {
        method: null,
        model: null,
        userRefs: [],
        prompt: null,
    },
    inspiration: {
        allbumlist: [],
        initialAlbumID: 'ebb61632-8c51-47b3-a2e2-8ccf4373bde4',
        activeAlbumRef: null,
        activeAlbum: {},
        activeAlbumCardList: []
    }
};

// AppContext is the high level data store context for the App
const AppContext = createContext(null);

export function AppProvider({ children }) {
    // Check to see if the user is authenticated before fetching data
    const { currentUser, updateUser } = useContext(AuthContext);
    const generateCompose = async (storyRef) => {
        if (!state.stories.generateStory) return;
        const composeId = nanoid(20);
        const compRef = doc(storyRef, 'compose', composeId);
        //await setDoc(userRef, {credits: increment(-state.stories.generateStory.cost)}, {merge:true}).then(async ()=>{
        dispatch({ type: "UPDATE_USER_CREDITS", payload: { credits: -state.stories.generateStory.cost } });
        await setDoc(compRef, state.stories.generateStory, { merge: true }).then(() => {
            dispatch({ type: "GENERATE_STORY", payload: { "generateStory": null } });
            dispatch({ type: "CLEAR_NEXT_STORY_METADATA", payload: {} });
            // dispatch({ type: "UPDATE_CURRENT_REFS" , payload: [] });
        }).catch((error) => {
            console.log('Error adding new compose for user: ', userRef.id, error)
        })
        //});
    }
    // Main reducer for App context
    const reducer = (draft, action) => {
        const storyRef = draft.stories.activeStory.ref
        const shotRef = ("payload" in action && action.payload?.shot_id != null && storyRef) ? doc(storyRef, 'shots', action.payload.shot_id) : (("payload" in action && action.payload?.shot?.id != null && storyRef)) ? doc(storyRef, 'shots', action.payload.shot.id) : null
        const takeRef = ("payload" in action && action.payload?.take_id != null && storyRef) ? doc(storyRef, 'takes', action.payload.take_id) : null

        // const userRef = (action.type.search("USER") > -1) ? await setDoc(doc(db, 'users').doc(currentUser.uid) : 0
        switch (action.type) {
            /*case "TOGGLE_USER_CAN_LISTEN_TO_TAKES":
                draft.app.settings.userCanListenToTakes = !draft.app.settings.userCanListenToTakes;
                return draft;*/


            case "PROCESS_FIRST_TAKE": {
                const { activeTakeList } = action.payload || {};
                if (activeTakeList && activeTakeList.length > 0) {
                    const firstTake = activeTakeList[0];
                    if (firstTake && firstTake.stream_path && !firstTake.clip_dur) {
                        getVideoDuration(firstTake.stream_path)
                            .then((duration) => {
                            })
                            .catch((error) => {
                                console.error("Error processing first take:", error);
                            });
                    }
                }
                return draft;
            }
            case "PROCESS_SELECTED_ITEM": {
                const { selectedItem } = draft;
                if (selectedItem && selectedItem.stream_path) {
                    getVideoDuration(selectedItem.stream_path)
                        .then((duration) => {
                            const shotRef = doc(storyRef, "shots", selectedItem.shot_id);
                            return updateDoc(shotRef, { shot_dur: duration })
                                .then(() => console.log("Shot updated successfully with duration."));
                        })
                        .catch((error) => {
                            console.error("Error processing selected take:", error);
                        });
                }
                return draft;
            }

            case "UPDATE_AUDIO": {
                const audio = action.payload;
                draft.stories.activeStory.audio = (audio && Object.keys(audio).length) > 0 ? audio : {};
                return draft;
            }

            case "UPLOAD_AUDIO": {
                const { file } = action.payload;
                if (!file) return;
                let audioDoc = {};
                audioUpload({ file, state: draft })
                    .then(async (uploadResult) => {
                        if (uploadResult?.audio_path) {
                            const audioDocRef = doc(storyRef, "audio", "0");
                            audioDoc = {
                                audio_path: uploadResult.audio_path,
                                file_name: file.name,
                                uploaded_at: serverTimestamp(),
                                uploader_id: currentUser.uid,
                            };
                            await setDoc(audioDocRef, audioDoc)
                            dispatch({ type: "UPDATE_AUDIO", payload: audioDoc });
                        }
                    })
                    .catch((error) => {
                        console.error("Error during audio upload process:", error);
                        dispatch({ type: "UPDATE_AUDIO", payload: {} });
                    });
                return draft;

            }
            case "REMOVE_AUDIO": {
                getDoc(doc(storyRef, 'audio', '0'))
                    .then(async (doc) => {
                        await deleteDoc(doc.ref);
                    })
                    .catch((error) => {
                        console.error("Error during audio removal:", error);
                    });
                const nullDoc = async () => {
                    const audioDocRef = doc(storyRef, "audio", "0");
                    await setDoc(audioDocRef, {})
                };
                nullDoc();
                draft.stories.activeStory.audio = null;
                return draft;
            }
            case "MIGRATE_AUDIO": {
                draft.app.migratingAudio = true;
                return draft;
            }
            case "END_MIGRATING_AUDIO": {
                draft.stories.activeStory.last_audio = null;
                draft.stories.activeStory.audio_file_name = null;
                draft.app.migratingAudio = null;
                return draft;
            }
            // toggle story star function
            case "TOGGLE_STORY_STAR": {
                //const { currentUser } = useContext(AuthContext);
                if (!currentUser) {
                    console.error("User is not authenticated");
                    return draft;
                }
                draft.app.toggleStoryStar = action.payload
                return draft
            }
            case "UPDATE_PART_TITLE": {
                const { parts, partId, title } = action.payload;
                if (!parts?.length) return draft;
                else {
                    const newParts = parts.map((part) => {
                        if (part.id === partId) {
                            return { ...part, title: title };
                        }
                        return part;
                    });
                    setDoc(storyRef, { "parts": newParts }, { merge: true })
                    draft.stories.activeStory.parts = newParts;
                }
                return draft;
            }
            case "UPDATE_STORY_PARTS":
                //draft.story.parts = action.payload
                draft.stories.activeStory.parts = action.payload
                const updateParts = action.payload;
                setDoc(storyRef, { "parts": updateParts }, { merge: true })
                return draft;
            case "UPDATE_STORY_SHOTS":
                const part_index = action.payload.part_index
                // draft.story.shots = [...draft.story.shots, ...action.payload.shots]
                // draft.story.parts[part_index].order = action.payload.shots.map(shot => shot.id)

                // we have the option to remove previous shots, or tag them as deleted
                // we need to delete if we want the counter to be accurate ...
                draft.stories.activeStory.parts[part_index].order.forEach((shot_id) => {
                    setDoc(storyRef, 'shots', shot_id,
                        { "deleted_at": serverTimestamp() }, { merge: true }
                    )
                })
                if (storyRef) {
                    action.payload.shots.forEach((shot) => {
                        const shotRef = doc(storyRef, 'shots', shot.id);
                        setDoc(shotRef, shot, { merge: true })
                            .then(() => shotRef)
                            .catch((error) => {
                                console.log('Error creating new shot:', shotRef)
                            })
                    })
                    var new_parts = draft.stories.activeStory.parts
                    new_parts[part_index].order = action.payload.shots.map(shot => shot.id)
                    setDoc(storyRef, { "parts": new_parts }, { merge: true })
                }
                return draft;

            case "UPDATE_STORY_TITLE":
                if (storyRef) {
                    setDoc(storyRef, { "title": action.payload.title }, { merge: true })
                }
                return draft

            case "UPDATE_STORY_DESCRIPTION":
                if (storyRef) {
                    setDoc(storyRef, { "description": action.payload.description }, { merge: true })
                }
                return draft

            case "START_STORY_TIMER":
                const timerStart = action.payload.timerStart

                if (storyRef) {

                    setDoc(storyRef, { "timerStart": timerStart }, { merge: true })
                }
                return draft

            case "TOGGLE_STORY_COMPETITION":
                const isCompetition = action.payload.isCompetition
                if (storyRef) {
                    setDoc(storyRef, { "isCompetition": isCompetition, "timerStart": null }, { merge: true })
                }
                return draft

            case "TOGGLE_PREVIEW_VOLUME":
                const previewVolume = action.payload.volume

                draft.app.previewVolume = previewVolume

                return draft
            case "TOGGLE_MUSIC_VOLUME":
                const musicVolume = action.payload.volume

                draft.app.musicVolume = musicVolume

                return draft
            case "ADD_STORY_SHOT":
                const { part_id, index, shot, auto_title } = action.payload;

                // Find the index of the part where the shot needs to be added
                const part_ind = draft.stories.activeStory.parts.findIndex(part => part.id === part_id);

                if (part_ind === -1) {
                    console.error("Part ID not found");
                    return draft; // Exit early if part_id is invalid
                }

                const parts = JSON.parse(JSON.stringify(draft.stories.activeStory.parts))
                // Update the order array in the specified part
                if (typeof index === "number") {
                    parts[part_ind].order = [
                        ...parts[part_ind].order.slice(0, index),
                        shot.id,
                        ...parts[part_ind].order.slice(index)
                    ];
                } else {
                    if (parts[part_ind].order.length === 0) parts[part_ind].order = [shot.id];
                    else parts[part_ind].order = [...parts[part_ind].order, shot.id];
                }

                if (auto_title) {
                    shot.header = "Shot " + parts[part_ind].order.length;
                }

                draft.stories.activeStory.parts = parts;
                // Write the new shot to Firestore and update the part order
                if (shot?.id) {
                    try {
                        const batch = writeBatch(db);

                        // Add the new shot to the "shots" collection
                        const shotRef = doc(storyRef, "shots", shot.id);
                        batch.set(shotRef, shot, { merge: true });

                        // Update the "parts" field in the story
                        batch.set(
                            storyRef,
                            { "parts": parts },
                            { merge: true }
                        );

                        // Commit the batch
                        batch.commit();
                        console.log("Shot added and parts updated successfully.");
                    } catch (error) {
                        console.error("Error creating or updating shot:", error);
                    }
                } else {
                    console.error("shotRef is not defined.");
                }

                return draft; // Immer will finalize the updated draft object

            case "MOVE_STORY_SHOT":
                const source_part_id = action.payload.source_part_id
                const dest_part_id = action.payload.dest_part_id
                const moving_shot_id = action.payload.moving_shot_id
                const preceding_shot_id = action.payload.preceding_shot_id

                var new_parts = draft.stories.activeStory.parts

                const source_part_index = new_parts.map(part => part.id).indexOf(source_part_id)
                if (source_part_index === -1) {
                    console.error("MOVE_STORY_SHOT non-existant part!", source_part_id)
                }

                const dest_part_index = new_parts.map(part => part.id).indexOf(dest_part_id)
                if (dest_part_index === -1) {
                    console.error("MOVE_STORY_SHOT non-existant part!", dest_part_index)
                }

                const dest_shot_index = preceding_shot_id === "000" ? 0 : new_parts[dest_part_index].order.indexOf(preceding_shot_id) + 1

                // first we remove the moving shot
                new_parts[source_part_index].order = new_parts[source_part_index].order.filter(id => id !== moving_shot_id)

                // then we add its id to the destination part
                // new_parts[dest_part_index].order = [...new_parts[dest_part_index].order, moving_shot_id] // at the end
                new_parts[dest_part_index].order.splice(dest_shot_index, 0, moving_shot_id) // where it was dropped

                setDoc(storyRef, { "parts": new_parts }, { merge: true })

                return draft;
            case "DELETE_STORY_SHOT":
                if (!storyRef || !shotRef) {
                    console.error("Missing storyRef for shot deletion");
                    return draft;
                }
                //const shot_ref = doc(storyRef, 'shots', action.payload.shot_id)
                // Create a copy of parts before starting async operation
                const partsBeforeDelete = JSON.parse(JSON.stringify(draft.stories.activeStory.parts));

                // Update local state immediately
                draft.stories.activeStory.parts = partsBeforeDelete.map(part => ({
                    ...part,
                    order: part.order.filter(id => id !== action.payload.shot_id)
                }));

                // Handle database updates
                (async () => {
                    try {
                        const delBatch = writeBatch(db);

                        // Mark shot as deleted
                        delBatch.set(shotRef, {
                            "deleted_at": serverTimestamp()
                        }, { merge: true });

                        // Update story parts
                        delBatch.set(storyRef, {
                            "parts": partsBeforeDelete.map(part => ({
                                ...part,
                                order: part.order.filter(id => id !== action.payload.shot_id)
                            }))
                        }, { merge: true });

                        // Commit the batch
                        await delBatch.commit();
                    } catch (error) {
                        console.error("Error deleting shot:", error);
                        // Revert local state on error
                        draft.stories.activeStory.parts = partsBeforeDelete;
                    }
                })();

                return draft;

            case "HIDE_STORY_SHOT":
                if (!storyRef) {
                    console.error("Missing storyRef or shotRef for shot hide");
                    return draft;
                }
                //const shotRef = doc(storyRef, 'shots', action.payload.shot.id)

                // Handle database updates
                setDoc(shotRef, { hidden: action.payload.shot.hidden ? false : true }, { merge: true });
                draft.stories.activeShotList = draft.stories.activeShotList.map(shot => shot.id === action.payload.shot_id ? { ...shot, hidden: action.payload.hide } : shot);

                return draft;

            case "HIDE_STORY_PART":
                if (!storyRef) {
                    console.error("Missing storyRef for part hide");
                    return draft;
                }
                // Handle database updates
                const newParts = draft.stories.activeStory.parts.map(part => part.id === action.payload.part_id ? { ...part, hidden: action.payload.hide } : part)
                setDoc(storyRef, { parts: newParts }, { merge: true });
                draft.stories.activeStory.parts = newParts;
                return draft;


            case "DELETE_STORYBOARD":
                const { boardRef } = action.payload;
                //const delete_storyboard_batch = boardRef.set();
                //const storyboardRef = await setDoc(doc(db, 'portals').where('story_id', '==', portal_id)

                setDoc(boardRef, { "deleted_at": serverTimestamp() }, { merge: true })

                return draft;





            case "DELETE_TAKE":
                //takeRef.update({"deleted_at": firebase.firestore.FieldValue.serverTimestamp()})
                //const delete_take_batch = db.batch()
                setDoc(takeRef, { "deleted_at": serverTimestamp() }, { merge: true })
                var shot_payload = {}
                if (action.payload.shot_selected_take_id == action.payload.take_id) {
                    shot_payload["selected_take_id"] = null
                    shot_payload["selected_take_stream_url"] = null
                    shot_payload["selected_take_im"] = null
                }
                if (action.payload.shot_last_take_id == action.payload.take_id) {
                    shot_payload["last_take_stream_url"] = null
                    shot_payload["last_take_im"] = null
                    shot_payload["last_take_id"] = null
                }
                if (Object.keys(shot_payload).length > 0) {
                    setDoc(shotRef, shot_payload, { merge: true })
                }

                //delete_take_batch.commit()

                return draft

            case "TRIM_TAKE":
                //takeRef.update({"deleted_at": firebase.firestore.FieldValue.serverTimestamp()})
                //const delete_take_batch = db.batch()
                setDoc(takeRef, { offset_ms: action.payload.offset_ms }, { merge: true })

                return draft

            case "UPDATE_STORY_SHOT_DESCRIPTION":
                // function replaceDescIfIdMatches(shot, payload) {
                //     if (shot.id === payload.shot_id) {shot.description = payload.description}
                // }
                // draft.story.shots.map(shot => replaceDescIfIdMatches(shot, action.payload))
                //const sid = action.payload.shot_id
                const desc = action.payload.description
                //let sref = /*shotRef !== undefined ? shotRef : */doc(storyRef, 'shots', sid);
                if (!shotRef) return;
                const updateShotDesc = async (sref, description) => {
                    await setDoc(sref, { "description": description }, { merge: true })
                }
                updateShotDesc(shotRef, desc);

                return draft

            case "MOVE_STORY_SHOT":

                // action.payload.shot_id

                // console.log('Proposed move:', (action.payload.shot_id, action.payload.source_part_id, action.payload.dest_part_id, action.payload.preceding_shot_id))

                //  similar logic as onDragEnd()  goes here

                return draft

            case "UPDATE_STORY_SHOT_TITLE":
                // function replaceTitleIfIdMatches(shot, payload) {
                //     if (shot.id === payload.shot_id) {shot.header = payload.text}
                // }
                // draft.story.shots.map(shot => replaceTitleIfIdMatches(shot,action.payload))
                const { shot_id, text } = action.payload;
                let ref = /*shotRef !== undefined ? shotRef : */doc(storyRef, 'shots', shot_id);
                if (!ref) return;
                const updateShotTitle = async (ref, title) => {
                    await setDoc(ref, { "header": title }, { merge: true })
                }
                updateShotTitle(ref, text);

                return draft

            case "UPDATE_STORY_STYLE":
                const style = action.payload.style;
                draft.stories.activeStory.storyStyle = style;
                setDoc(storyRef, { "storyStyle": style }, { merge: true })
                return draft

            case "UPDATE_STORY_MODE":
                const mode = action.payload.mode;
                const vertical = (mode === "vertical") ? true : false
                draft.stories.activeStory.is_vertical = vertical;
                setDoc(storyRef, { "is_vertical": vertical }, { merge: true })
                return draft

            case "CREATE_VENEER_FOR_TAKE":
                draft.app.createVeneer = action.payload
                return draft

            case "CLEAR_CREATE_VENEER":
                draft.app.createVeneer = null
                return draft

            /* OLD STORY SHOT TITLE UPDATE
            case "UPDATE_STORY_SHOT_TITLE":
                const { shot_id, text } = action.payload;
                if (shot_id && text !== undefined) {
                    draft.story.shots.forEach(shot => {
                        if (shot.id === shot_id) {
                            shot.header = text;
                        }
                    });
            
                    setDoc(shotRef, { header: text }, {merge:true})
                }
                //         .then(() => {
                //             // console.log("Document successfully updated!");
                //         })
                //         .catch(error => {
                //             // console.error("Error updating document: ", error);
                //         });
                // } else {
                //     // console.error("Invalid shot_id or text:", shot_id, text);
                // }
            
                return draft
            
             */



            case "UPDATE_STORY_SHOT_PROMPT":
                setDoc(shotRef, { "prompt": action.payload.prompt, "prompt_composition": action.payload.composition, "prompt_style": action.payload.style }, { merge: true })
                return draft

            case "UPDATE_USER_CREDITS":
                if (!currentUser) return draft;
                draft.app.updateCredits = action.payload.credits;
                if (action.payload.credits && action.payload.credits !== 0) {
                    draft.userData.credits = draft.userData.credits + action.payload.credits;
                }
                return draft
            case "CLEAR_LAST_CREDITS":
                draft.userData.lastCredits = null;
                return draft

            case "UPDATE_STORY_SHOT_IMAGE":
                // function replaceImageIfIdMatches(shot, payload) {
                //     if (shot.id === payload.shot_id) {shot.image = payload.image}
                // }
                // draft.story.shots.map(shot => replaceImageIfIdMatches(shot,action.payload))
                setDoc(shotRef, { "image": action.payload.image }, { merge: true })

                return draft
            /*case "UPDATE_SHOT_SELECTED_TAKE_IM": // possibly depricate in favor of UPDATE_SHOT_SELECTED_TAKE_IM
                setDoc(shotRef, {"last_take_im": action.payload.image}) // this was working

                return draft
            case "UPDATE_SHOT_EMPTY_TAKE":
                    setDoc(shotRef, {"selected_take_id": null, "last_take_id": null, "last_take_im": null, "last_take_stream_url": null, "image": action.payload.image}) // proposed new
                    // setDoc(shotRef, {"selected_take_im": action.payload.image})  maybe we add this logic ... selected_take_id
    
                    return draft
            */

            case "UPDATE_SHOT_LAST_TAKE":
                //const sRef = doc(storyRef, 'shots', action.payload.shot_id)
                setDoc(shotRef, { "last_take_id": action.payload.take_id, "last_take_im": action.payload.image, "last_take_stream_url": action.payload.stream_path }, { merge: true }) // proposed new
                    .catch((error) => {
                        console.log('Error updating shot last take:', shotRef)
                    })
                return draft

            case "UPDATE_SHOT_SELECTED_TAKE":
                //const shotRef = doc(storyRef, 'shots', action.payload.shot_id)
                setDoc(shotRef, { "selected_take_id": (action.payload.image) ? action.payload.take_id : null, "selected_take_im": action.payload.image, "selected_take_stream_url": action.payload.stream_path }, { merge: true }) // proposed new
                // setDoc(shotRef, {"selected_take_im": action.payload.image})  maybe we add this logic ... selected_take_id
                return draft

            case "FORK_STORY":
                draft.app.forkingStory = action.payload
                return draft;

            case "CLEAR_FORK":
                draft.app.forkingStory = null;
                return draft;

            case "CHECK_MOBILE":
                draft.app.isMobileSize = isMobileSize();
                return draft;

            case "UPDATE_PORTAL_PASS":
                const portalRef = doc(db, 'portals', action.payload.portal.id)
                setDoc(portalRef, { "requirePass": action.payload.requirePass }, { merge: true }).then(() => {
                    dispatch({ type: "UPDATE_PORTAL", payload: { portal: { ...action.payload.portal, ...{ "requirePass": action.payload.requirePass } } } });

                })
                    .catch((error) => {
                        console.log('Error updating portal pass:', action.payload.portal, action.payload.requirePass, error)
                    })
                return draft;

            case "STORY_IMPORT":
                draft.app.storyImport = action.payload


                return draft;

            case "ADD_TAKE_TO_SHOT":
                // push take to the cloud under SHOT
                const payload = action.payload;
                //const activeStoryRef = draft.stories.activeStoryRef   // this gets cleared
                if (storyRef) {
                    const uploader_id = (currentUser) ? currentUser.uid : "anon"
                    const take = {
                        video_path: payload.video_path,
                        thumbnail: payload.thumbnail,
                        id: payload.take_id,
                        shot_id: payload.shot_id,
                        uploaded_at: serverTimestamp(),
                        //modified_at: Date.now(), 
                        uploader_id: uploader_id,
                        proxyLUT: draft.app.settings.proxyIsBlackAndWhite ? "gray_strong_contrast" : "none",
                        deleted_at: null,
                        source: payload.source,
                        is_ref: payload.is_ref || false,
                        is_vertical: payload.is_vertical || false,
                    };


                    //dur_ms
                    //est_created_time - from filename? or
                    //  // payload.user_id  .. why are these undefined!?  vs e.g. ahbMAd47J1aWnEhDrq5PwjQSmBU2


                    // I get the sense that this may be the way to go for large projects, with GET instead of snapshot
                    // but having N listeners for each participant seems overkill. 
                    //    this code remains here so it could be reanble for testing at a future date
                    //    As of Sept 2021 - Mar 2022 (including okapi-webdev data), this was mirrored in both places.
                    //    This state is not properly maintained for subsequent changes, like thumbnail updates, etc.
                    //  In Mar 2022, including all of 10zebra data, this was depricated.
                    //
                    // const takeRefALT = storyRef.collection('shots').doc(payload.shot_id).collection('takes').doc(payload.take_id)
                    // takeRefALT.set(take)
                    //    .then(() => takeRefALT)
                    // .catch((error) => {
                    //   console.log('Error creating new take:', takeRefALT)
                    // })

                    // we are actually using these takes for rendering the active state in the app
                    const takeRef = doc(storyRef, 'takes', payload.take_id)
                    setDoc(takeRef, take, { merge: true })
                        .then(() => {
                            if (take.generate) {
                                setDoc(takeRef, { generate: false }, { merge: true })
                                dispatch({ type: "START_VENEER", payload: {} });
                            }
                            return takeRef
                        })
                        .catch((error) => {
                            console.log('Error creating new take:', takeRef)
                        })
                    //console.log("ADD_TAKE_TO_SHOT  cloud ", payload.take_id)

                } else {
                    console.log("WARNING! Cant add take to story", draft.stories.activeStory)
                }

                // function addTakeIfIdMatches(shot, payload) {
                //     if (shot.id === payload.shot_id) {
                //         shot.takes = shot.takes || [];
                //         shot.takes.push({video_path: payload.video_path, thumbnail: payload.thumbnail})
                //     }
                // }
                // draft.story.shots.map(shot => addTakeIfIdMatches(shot,action.payload))
                return draft

            case "ADD_IMAGE_TO_SHOT":
                // push image to the cloud, we will treat it as a video with 1 second log, plus some meta data
                const im_payload = action.payload;
                if (storyRef) {
                    const uploader_id = (currentUser) ? currentUser.uid : "anon"
                    const take = {
                        image_path: im_payload.image_path,
                        // video_path: payload.video_path,  // should we add an loading video until we replace it?
                        thumbnail: im_payload.thumbnail,
                        id: im_payload.take_id,
                        shot_id: im_payload.shot_id,
                        uploaded_at: serverTimestamp(),
                        //modified_at: Date.now(), 
                        uploader_id: uploader_id,
                        proxyLUT: draft.app.settings.proxyIsBlackAndWhite ? "gray_strong_contrast" : "none",
                        is_image: true,
                        is_ref: im_payload.is_ref || false,
                        is_vertical: im_payload.is_vertical || false,
                        source: im_payload.source || "upload",
                        deleted_at: null
                    };

                    const takeRef = doc(storyRef, 'takes', im_payload.take_id)
                    setDoc(takeRef, take, { merge: true })
                        .then(() => takeRef)
                        .catch((error) => {
                            console.log('Error creating new take:', takeRef)
                        })
                } else {
                    console.log("WARNING! Cant add image take to story", draft.stories.activeStory)
                }

                return draft

            /*case "ADD_TAKE_AS_REF":
                // push image to the cloud, we will treat it as a video with 1 second log, plus some meta data
                const ref_payload = action.payload;
                if (storyRef) {
                    const uploader_id = (currentUser) ? currentUser.uid : "anon"
                    const take = {image_path: ref_payload.image_path, 
                         video_path: ref_payload.video_path,  // should we add an loading video until we replace it?
                        thumbnail: ref_payload.thumbnail,
                        id: ref_payload.take_id,
                        shot_id: ref_payload.shot_id,
                        uploaded_at: serverTimestamp(),
                        //modified_at: Date.now(), 
                        uploader_id: uploader_id,
                        proxyLUT : draft.app.settings.proxyIsBlackAndWhite ? "gray_strong_contrast" : "none",
                        is_image: true,
                        is_ref: true,
                        source: ref_payload.source || "upload", 
                        deleted_at: null}; 
                    
                const takeRef = doc(storyRef, 'takes', ref_payload.take_id)
                setDoc(takeRef, take, {merge:true})
                    .then(() => takeRef)
                .catch((error) => {
                    console.log('Error creating new take:', takeRef)
                })
                } else {
                    console.log("WARNING! Cant add image take to story", draft.stories.activeStory)
                }

                return draft
            */
            case "ADD_TAKE_REF":
                draft.app.newRefTake = action.payload;
                return draft;

            case "CLEAR_TAKE_REF":
                draft.app.newRefTake = null;
                return draft;

            case "UPDATE_TAKE":
                if (!storyRef) return;
                const update_payload = action.payload;
                const updateRef = doc(storyRef, 'takes', update_payload.id)
                setDoc(updateRef, update_payload, { merge: true })
                    //.then(() => updateRef)
                    .catch((error) => {
                        console.log('Error updating take:', updateRef)
                    })
                return draft;

            case "ADD_TMP_REF":
                draft.app.tmpRef = action.payload;
                return draft;

            case "CLEAR_TMP_REF":
                draft.app.tmpRef = null;
                return draft;

            case "CLEAR_USER_REFS":
                draft.generate.userRefs = null;
                // draft.generate.userRefs = [];
                return draft;

            case "CLEAR_USER_STATE":
                draft.app.updateUserState = null;
                return draft;

            case "UPDATE_USER_STATE":
                draft.app.updateUserState = action.payload;
                return draft;

            case "UPDATE_USER_REFS":
                draft.generate.userRefs = action.payload;
                // draft.stories.activeStory.currentRefs = action.payload;
                return draft;

            case "UPDATE_SELECTED_METHOD":
                draft.generate.method = action.payload;
                return draft;

            case "UPDATE_SELECTED_OUTPUT":
                draft.generate.output = action.payload;
                return draft;

            case "UPDATE_SELECTED_MODEL":
                draft.generate.model = action.payload;
                return draft;

            case "UPDATE_PROMPT":
                draft.generate.prompt = action.payload;
                return draft;

            case "IS_HUNT":
                draft.app.custom.is_hunt = action.payload;
                return draft;

            case "ADD_IMAGE_TO_TMP":
                // push image to the cloud, we will treat it as a video with 1 second log, plus some meta data
                const img_payload = action.payload;
                if (storyRef) {
                    const uploader_id = (currentUser) ? currentUser.uid : "anon"
                    const take = {
                        image_path: img_payload.image_path,
                        // video_path: payload.video_path,  // should we add an loading video until we replace it?
                        thumbnail: img_payload.thumbnail,
                        id: img_payload.take_id,
                        uploaded_at: serverTimestamp(),
                        //modified_at: Date.now(), 
                        uploader_id: uploader_id,
                        proxyLUT: draft.app.settings.proxyIsBlackAndWhite ? "gray_strong_contrast" : "none",
                        deleted_at: null
                    };

                    const takeRef = doc(storyRef, 'takes', img_payload.take_id)
                    setDoc(takeRef, take, { merge: true })
                        .then(() => takeRef)
                        .catch((error) => {
                            console.log('Error creating new take:', takeRef)
                        })
                } else {
                    console.log("WARNING! Cant add image take to story", draft.stories.activeStory)
                }

                return draft;

            case "MOVE_TAKE_TO_SHOT":

                const destShotRef = ("payload" in action && "dest_shot_id" in action.payload) ? doc(storyRef, 'shots', action.payload.dest_shot_id) : null
                //const oldShotRef = ("payload" in action && "shot_id" in action.payload) ? storyRef.collection('shots').doc(action.payload.shot_id) : null

                setDoc(takeRef, { "shot_id": action.payload.dest_shot_id, "modified_at": serverTimestamp() }, { merge: true })

                setDoc(destShotRef, { "last_take_id": action.payload.take_id, "last_take_im": action.payload.image, "last_take_stream_url": action.payload.stream_path }, { merge: true })
                // if dest shot has selected shot
                var shot_payload = {}
                if (action.payload.prev_selected_take_id == action.payload.take_id) {
                    shot_payload["selected_take_id"] = null
                    shot_payload["selected_take_stream_url"] = null
                    shot_payload["selected_take_im"] = null
                }
                if (action.payload.prev_last_take_id == action.payload.take_id) {
                    shot_payload["last_take_stream_url"] = null
                    shot_payload["last_take_im"] = null
                    shot_payload["last_take_id"] = null
                }
                if (Object.keys(shot_payload).length > 0) {
                    setDoc(shotRef, shot_payload, { merge: true })
                }

                return draft
            // case "UPDATE_NUM_SHOTS":
            //     draft.story.num_shots = action.payload.num_shots;
            //     return draft;
            case "RESET_STATE":
                draft = initialState;
                return draft;

            case "DO_USER_INIT":
                doUserInit();
                return;

            case "SAVE_CUT":
                draft.app.savingCut = action.payload;

                return draft;
            case "SET_PROMPT":
                draft.generate.prompt = action.payload.prompt;
                return draft;

            case "CLEAR_LAST_CUT":
                draft.stories.activeStory.last_cut = null
                // storyRef.update{}
                return draft;
            case "CLEAR_SAVE_CUT":
                draft.app.savingCut = null
                // storyRef.update{}
                return draft;
            case "CLEAR_LAST_EXPORT":
                draft.stories.activeStory.last_export = null
                return draft;
            case "CLEAR_LAST_ACTIVE":
                draft.stories.setLastActive = null;
                return draft;
            case "APP_LOADED":
                draft.app.isLoading = false;
                return draft;
            case "MOVE_TAKE":
                draft.app.movingTake = action.payload.moving;
                return draft;
            case "ADD_ALERT": // Add alert to the list of active alerts
                // type: success, info, warning, error
                // message: string
                draft.app.alerts[Date.now()] = action.payload.alert;
                return draft;
            case "CLEAR_ALERT":
                delete draft.app.alerts[action.payload.key];
                return draft;
            case "MODAL":  // Set open/closed draft of App dialogs
                draft.app.modal = action.payload;
                return draft;
            case "CONTROL":  // Top level draft of app controls
                draft.app.controls[action.payload.name] = action.payload.state;
                return draft;
            case "RESET_FLIP":  // Reset flip initiator
                draft.app.controls.flip = false;
                return draft;
            case "FLIP_SHOTS":  // Initiate card flip sequence
                draft.app.controls.flip = true;
                return draft;
            case "SET_PREVIEW_IMAGE":  // Insert the preview into draft.cardPreviews with the cardID as the key
                // args: cardID, src
                {
                    let preview = {
                        src: action.payload.src,
                        orientation: action.payload.orientation,
                    }
                    draft.app.cardPreviews[action.payload.cardID] = preview;
                    return draft;
                }
            case "CLEAR_PREVIEW_IMAGE":  // Delete the image keyed off the cardID
                // args: cardID
                delete draft.app.cardPreviews[action.payload.cardID]
                return draft;
            case "START_UPLOAD":  // Configure and open upload modal
                logEvent(analytics, 'START_UPLOAD');
                const upload = {
                    data: {
                        totalFiles: action.payload.totalFiles,
                        totalBytes: action.payload.totalBytes,
                        uploadCount: 0,
                        failedCount: 0,
                        failedNames: [],
                        fileBytes: {},
                    }
                }
                draft.app.upload = upload;
                return draft;
            case "FILE_UPLOAD_UPDATE": {
                if (draft.app.upload) {
                    draft.app.upload.data.fileBytes[action.payload.cardID] = action.payload.bytes;
                }
                return draft;
            }
            case "FILE_UPLOAD_SUCCESS":  // Register one successful file uploaded
                if (draft.app.upload) {
                    draft.app.upload.data.uploadCount++;
                    draft.app.contributedCount = draft.app.contributedCount + draft.app.upload.data.totalFiles;
                    const story = draft.stories.activeStory;
                    logEvent(analytics, 'FILE_UPLOAD_SUCCESS'); // , { count: draft.app.contributedCount});
                    // TODO: Clean up cards for failed uploads and notify the user
                    // Even if they have a preview image, they need an original image to be valid
                    if (draft.app.upload.data.uploadCount + draft.app.upload.data.failedCount === draft.app.upload.data.totalFiles) {
                        draft.app.upload = null
                    }
                    /*if (story.isCompetition && (story.timerStart == undefined || story.timerStart == null)) {
                        const timerStart = Date.now();
                        const payload = { timerStart: timerStart };
                        if(dispatch) dispatch({ type: "START_STORY_TIMER", payload: payload });
                    }*/
                }
                return draft;
            case "FILE_UPLOAD_FAILURE":  // Register a failed file upload
                // args: fileName
                if (draft.app.upload) {
                    draft.app.alerts[Date.now()] = {
                        type: 'error',
                        message: `Upload failed for ${action.payload.fileName}`,
                    }
                    draft.app.upload.data.failedCount++;
                    draft.app.upload.data.failedNames.push(action.payload.fileName)
                    logEvent(analytics, 'FILE_UPLOAD_FAILURE') //, { filename: action.payload.fileName}); 

                    // Even if they have a preview image, they need an original image to be valid
                    if (draft.app.upload.data.uploadCount + draft.app.upload.data.failedCount === draft.app.upload.data.totalFiles) {
                        // TODO: Open file failure modal to show names and let the user confirm
                        draft.app.upload = null
                    }
                } else {
                    console.error('Attempt to register file upload failure but file upload modal is not open.')
                }
                return draft;
            case "START_IMPORT":
                logEvent(analytics, 'START_IMPORT');
                const importing = {  // could update to frames processed ... not really required. 
                    data: {
                        totalFrames: 100,
                        processedFrames: 10,
                    }
                }
                draft.app.importing = importing;
                return draft;
            case "IMPORT_SUCCESS":
                // this just ends alert for now
                if (draft.app.importing) {
                    draft.app.importing = null
                }
                return draft;
            case "UPDATE_PORTAL":
                draft.app.portal = action.payload;
                return draft;
            case "UPDATE_ACTIVE_SHOT":
                //draft.stories.activeShotID = action.payload.shot_id;
                return draft;
            case "ADD_REQUESTED_PORTAL":
                draft.app.portal.requested_id = action.payload.portal_id;
                return draft;
            case "CLEAR_REQUESTED_PORTAL":
                draft.app.portal.requested_id = null;
                return draft;
            case "UPDATE_ACTIVE_STORY_REF":
                draft.stories.setLastActive = (action.payload.setLastActive) ? action.payload.setLastActive : null;
                draft.stories.activeStoryRef = action.payload.activeStoryRef;
                return draft;
            case "SET_LAST_ACTIVE":
                draft.stories.lastActiveStory = action.payload.portal_id;
                return draft;
            case "CLEAR_ACTIVE_STORY_REF":
                draft.stories.activeStoryRef = null;
                draft.stories.activeStory = {}
                draft.stories.activeShotList = [];
                draft.stories.activeTakeList = [];
                draft.stories.activeRefList = [];
                return draft;
            case "CLEAR_ACTIVE_ALBUM_REF":
                draft.inspiration.activeAlbumRef = null;
                draft.inspiration.activeAlbum = {}
                draft.inspiration.activeAlbumCardList = [];
                return draft;
            case "UPDATE_USER_DATA":
                draft.userData = action.payload.userData;
                return draft;
            case "GET_USER_DATA":
                draft.userData.getUserData = action.payload.getUserData;
                return draft;
            case "UPDATE_STORY_LIST":
                draft.stories.storyList = action.payload.storyList;
                return draft;
            case "UPDATE_SHARED_STORY_LIST":
                draft.stories.sharedStoryList = action.payload.sharedStoryList;
                return draft;
            case "NEW_ACTIVE_STORY":
                draft.stories.nextStoryMetadata = action.payload.newStory
                //draft.stories.activeStory = action.payload.newStory;
                return draft;
            case "CLEAR_NEXT_STORY_METADATA":
                draft.stories.nextStoryMetadata = null
                return draft;
            case "UPDATE_ACTIVE_STORY":
                //draft.app.userIsStoryOwner = false
                draft.stories.activeStory = action.payload.activeStory;
                return draft;
            case 'SET_USER_IS_STORY_OWNER':
                draft.app.userIsStoryOwner = action.payload.userIsStoryOwner;
                return draft;
            case "UPDATE_ACTIVE_SHOT_LIST":
                draft.stories.activeShotList = action.payload.activeShotList;
                return draft;
            case "UPDATE_ACTIVE_REF_LIST":
                draft.stories.activeRefList = action.payload.activeRefList;
                return draft;
            case "UPDATE_ACTIVE_TAKE_LIST":
                draft.stories.activeTakeList = action.payload.activeTakeList;
                return draft;
            case "UPDATE_ACTIVE_VENEER_LUT":
                const VeneerLUT = {};
                action.payload.activeVeneerList.forEach((vn) => {
                    VeneerLUT[vn.ref.id] = vn
                })
                draft.stories.activeVeneerLUT = VeneerLUT
                return draft;
            case "UPDATE_STORY_SETTINGS":
                let new_settings = draft.app.settings
                for (const key in action.payload.settings) {
                    new_settings[key] = action.payload.settings[key]
                }
                draft.app.settings = new_settings
                draft.app.newSettings = action.payload.new || false
                return draft
            case "CLEAR_NEW_SETTINGS":
                draft.app.newSettings = false
                return draft
            case "UPDATE_STORY_MODELS":
                const { defaultModels, disallowedModels } = action.payload;
                const models = defaultModels ? { defaultModels: defaultModels } : disallowedModels ? { disallowedModels: disallowedModels } : null;
                if (models) setDoc(storyRef, models, { merge: true });
                return draft;
            case "UPDATE_PRIORITIZE_LIVE_PREVIEW":
                draft.app.prioritizeLivePreview = action.payload.prioritizeLivePreview;
                return draft
            case "UPDATE_ALBUM_SOURCE_SET":
                const newSourceSet = new Set();
                action.payload.sourceSet.forEach((sourceID) => {
                    newSourceSet.add(sourceID)
                })
                draft.stories.albumSourceSet = newSourceSet;
                return draft;
            case "UPDATE_ACTIVE_ALBUM_SHOT_LIST":
                draft.inspiration.activeAlbumCardList = action.payload.activeAlbumCardList;
                return draft;
            case "UPDATE_ALBUM_LIST":
                draft.inspiration.albumList = action.payload.albumList;
                return draft;
            case "UPDATE_ACTIVE_ALBUM_REF":
                draft.inspiration.activeAlbumRef = action.payload.activeAlbumRef;
                return draft;
            case "UPDATE_ACTIVE_ALBUM":
                draft.inspiration.activeAlbum = action.payload.activeAlbum;
                return draft;
            case "SET_STARRED_STORIES":
                draft.stories.starredStories = action.payload.starredStories;
                return draft;
            case "GENERATE_STORY":
                draft.stories.generateStory = action.payload.generateStory;
                return draft;
            case "FORK_FROM_PORTAL":
                draft.app.initAndFork = { portalId: action.payload.portalId, fork: action.payload.fork }
                return draft;
            case "CLEAR_INIT_AND_FORK":
                draft.app.initAndFork = null;
                return draft;
            default:
                return draft;
        }
    };
    const [state, dispatch] = useImmerReducer(reducer, initialState);
    let intervalCount = 0;
    let interval = null;

    // RESET STATE
    // Only reset when a *logged-in* user's UID doesn't match the stored userData.
    // Do NOT reset on anonymous (no currentUser) loads — portal pages work without login
    // and the old "!currentUser || !userData.id" guard was clearing portal state on every
    // anonymous load, causing an infinite re-fetch loop.
    useEffect(() => {
        if (!currentUser) return; // anonymous visit — leave state intact
        if (state.userData.id && state.userData.id !== currentUser.uid) {
            // Different user logged in — reset everything and re-initialize
            dispatch({ type: "RESET_STATE" });
            doUserInit();
        }
    }, [dispatch, currentUser, state.userData?.id]);

    // Get Fresh User Data
    useEffect(() => {
        if (state.userData.getUserData || state.userData.lastCredits) {
            const userData = getUserData();
            //dispatch({ type: "UPDATE_USER_DATA", payload: { userData: userData } });
            if (userData.lastCredits && userData.lastCredits != userData.credits && userData.processingCredits == false) {
                if (interval) clearInterval(interval);
                dispatch({ type: "CLEAR_LAST_CREDITS", payload: {} });
                if (state.userData.getUserData) dispatch({ type: "GET_USER_DATA", payload: { getUserData: false } })
                const params = new URLSearchParams(window.location.search);
                const newCredits = params.get("newCredits");
                if (newCredits) {
                    params.delete("newCredits");
                    history.replace({ search: params.toString() });
                }
            }
        }
    }, [state.userData.getUserData, state.userData.lastCredits]);

    const getUserData = async (uDoc) => {
        let user, newDoc = uDoc;
        if (!currentUser) { return }
        //let unSubscribe = () => { };
        try {
            const userRef = doc(db, 'users', currentUser.uid);
            //let doc = uDoc;
            if (!newDoc || !newDoc.exists()) {
                await getDoc(userRef).then((uDoc) => {// db.database().ref('portal/' + portal_id + '/story_path');
                    newDoc = uDoc;
                })
            }
            if (!newDoc || !newDoc.exists()) return;
            let userData = newDoc.data();
            if (userData.processingCredits == true) {
                console.log("PROCESSING CREDITS - WAITING", intervalCount);
                if (!interval) interval = setInterval(getUserData, 1000);
                else if (intervalCount >= 5) {
                    clearInterval(interval)
                    dispatch({ type: "GET_USER_DATA", payload: { getUserData: false } })
                    await setDoc(userRef, { processingCredits: false, lastCredits: null }, { merge: true });
                    console.log("PROCESSING ERROR: Clearing processingCredits after 5 tries")
                }
                else intervalCount = intervalCount + 1;

                return;
            } else {
                if (interval) clearInterval(interval);
            }
            if (!userData.email) return;
            //if(Object.keys(userData.ref).length) return
            userData.ref = newDoc;
            userData.superuser = false;
            userData.zebradmin = false;
            userData.photoURL = currentUser.photoURL;
            if (userData.email === 'pmm3123@gmail.com') {
                userData.superuser = true;
            }
            if (!userData.account) {
                // old accounts are set to free and 100 credits
                userData.account = "free";
            }
            if (userData.email.indexOf("@10zebra.com") != -1 || userData.email.indexOf("@apollofractional.com") != -1) {
                userData.zebradmin = true;
                if (userData.credits < 50) {
                    userData.credits = 500;
                }
            }
            dispatch({ type: "UPDATE_USER_DATA", payload: { userData: userData } });
            return userData;
        } catch (error) {
            console.log('Error getting user data: ', error)
        } finally {
            console.log("user init complete finally")
            //return () => unSubscribe();
        }
    }

    const getPortal = async (portal_id) => {
        try {
            const portalRef = doc(db, 'portals', portal_id);
            let portal = {};
            await getDoc(portalRef).then((pRef) => {
                if (pRef.exists()) {
                    const data = pRef.data();
                    const user_id = data.creator_id;
                    const story_id = data.story_id;
                    const requirePass = data.requirePass;
                    portal = { id: portal_id, creator_id: user_id, story_id: story_id, requirePass: requirePass };
                }
            })
            return portal;
        } catch (error) {
            console.log('Error getting portal data: ', error);
            return {};
        }
    }

    const checkPortalPass = async (portal_id, returnPortal) => {
        let passed = false;
        let portal = {};
        const checkPass = (pass) => {
            if (pass === "" || pass === null || pass === undefined) return true;
            const passwords = JSON.parse(sessionStorage.getItem('passwords') || '[]');
            const found = passwords.find(p => p.id === portal_id && p.password === pass);
            return found !== undefined;
        }
        if (state.app.portal?.id && portal_id === state.app.portal.id) {
            passed = checkPass(state.app.portal.requirePass);
        } else {
            const portal = await getPortal(portal_id);
            dispatch({ type: "UPDATE_PORTAL", payload: portal });
            passed = checkPass(portal.requirePass);
        }
        return returnPortal ? { portal: portal, passed: passed } : passed;
    }


    // define some async functions that are used in multiple Effects: initial user setup and new project creation
    const initializeFromPortal = async (portal_id, fork, setActive, cachedPortalData) => {
        const userRef = currentUser ? doc(db, 'users', currentUser.uid) : null;
        try {
            // Fix 2: Use cached portal data if available, avoiding duplicate Firestore fetch
            let portal = cachedPortalData || null;
            if (!portal) {
                const portalRef = doc(db, 'portals', portal_id);
                const pRef = await getDoc(portalRef);
                portal = pRef.exists() ? pRef.data() : null;
            }
            if (!portal) return null;
            const storyRef = doc(db, 'users', portal.creator_id, 'stories', portal.story_id);
            if (!storyRef) {
                return null;
            }
            if (setActive) {
                // Fix 1: Fetch story doc and settings doc in parallel
                const settingsRef = doc(storyRef, 'settings', '0');
                const [sDoc, settings] = await Promise.all([
                    getDoc(storyRef),
                    getDoc(settingsRef)
                ]);
                if (sDoc.exists() && !sDoc.data().deleted_at) {
                    const activeStoryRef = sDoc.ref;
                    if (settings.exists()) {
                        dispatch({ type: "UPDATE_STORY_SETTINGS", payload: { settings: settings.data() } });
                    }
                    dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner: currentUser?.uid == portal.creator_id } });
                    dispatch({ type: "UPDATE_ACTIVE_STORY_REF", payload: { 'activeStoryRef': activeStoryRef } });
                    currentUser?.uid == portal.creator_id ? logEvent(analytics, 'OWNER_PORTAL_LOAD') : logEvent(analytics, 'COMMUNITY_PORTAL_LOAD');
                } else {
                    console.log("Story? No such document!");
                    return null;
                }
            } else {
                // Non-setActive paths (fork, read-only) still need the story doc
                const sDoc = await getDoc(storyRef);
                if (sDoc.exists() && !sDoc.data().deleted_at) {
                    const data = sDoc.data();
                    if (fork) {
                        let forkObj = structuredClone(fork);
                        forkObj.parts = data.parts;
                        forkObj.description = data.description || null;
                        if ("audio" in data) {
                            forkObj["audio"] = data.audio;
                        }
                        forkObj["last_cut"] = data.last_cut;
                        if (userRef) {
                            state.app.userIsStoryOwner ? logEvent(analytics, 'OWNER_FORK') : logEvent(analytics, 'COMMUNITY_FORK');
                            dispatch({ type: "FORK_STORY", payload: { forkingStory: forkObj, mediaOption: "All Media", userRef: userRef, storyRef: storyRef, newStory: forkObj } });
                            dispatch({ type: "CLEAR_FORK", payload: {} })
                        }
                        return null;
                    } else {
                        let story = data;
                        story.ref = sDoc.ref;
                        return story;
                    }
                } else {
                    console.log("Story? No such document!");
                    return null;
                }
            }
            return null;
        } catch (error) {
            console.log('Error querying data for portal defined Story: ', error);
            return null;
        }
    };

    useEffect(() => {
        const run = async () => {
            if (!currentUser) return;
            if (!state.stories.storyList) return;
            if (state.stories.starredStories.length) return;
            const portals = await getDocs(collection(db, 'users', currentUser.uid, 'starred_portals'));
            const docs = (portals) ? portals.docs : [];
            let starredBoards = [];
            if (docs.length) {
                const promises = docs.map(async (doc) => {
                    if (doc.exists()) {
                        const data = doc.data();
                        if (!data) return;
                        if (state.stories.storyList && state.stories.storyList.find(story => story.portal_id === data.portal_id) !== undefined) {
                            starredBoards.push(state.stories.storyList.find(story => story.portal_id === data.portal_id));
                        } else {
                            const resp = await initializeFromPortal(data.portal_id);
                            if (resp) starredBoards.push(resp) // = [...starredBoards, ...[resp]];
                        }
                    }
                });
                await Promise.all(promises);
            }
            dispatch({ type: "SET_STARRED_STORIES", payload: { starredStories: starredBoards } });
        };
        run();
    }, [currentUser, state.stories.storyList]); // currentUser, 

    const setupInitialStory = async (userRef) => {
        const storyRef = doc(userRef, 'stories');
        const newStory = initialStory; // storyConstructor();

        newStory.shots.forEach((shot, key) => {
            const shotRef = doc(storyRef, 'shots', shot.id);
            setDoc(shotRef, shot, { merge: true })
                .then(() => shotRef)
                .catch((error) => {
                    console.log('Error creating new shot:', shotRef)
                })
        })

        return setDoc(storyRef, newStory, { merge: true })
            .then(() => storyRef)
            .catch((error) => {
                console.log('Error creating new story for user:', userRef.id)
                return error
            })
    }

    const setupNewUser = async (user) => {
        const storyId = nanoid(20);
        const name = user.displayName || "";
        const newUser = {
            name: user.displayName,
            email: user.email,
            account: "free",
            credits: 250,
            //lastActiveStory: storyRef,
            created_at: serverTimestamp(),
            updated_at: serverTimestamp(),
            deleted_at: null,
        }
        const addUser = async () => {
            const userRef = doc(db, 'users', user.uid);
            await setDoc(userRef, newUser, { merge: true })
            const userDoc = await getDoc(userRef)
            if (userDoc.exists()) {
                let data = userDoc.data();
                data.ref = userDoc;
                dispatch({ type: "UPDATE_USER_DATA", payload: { userData: data } });
            }
            logEvent(analytics, "NEW_USER: " + user.email);
        }
        addUser();
    }

    const doUserInit = async (do_active_story_init) => {
        if (!currentUser) { return }
        //let unSubscribe = () => { };
        try {
            const userRef = doc(db, 'users', currentUser.uid);
            const user = await getDoc(userRef).then((uDoc) => {// db.database().ref('portal/' + portal_id + '/story_path');
                if (uDoc.exists()) {
                    let userData = getUserData(uDoc);
                    userData.ref = uDoc;
                    if (do_active_story_init && userData.lastActiveStory) {
                        const initStoryRef = async () => {
                            const arr = userData.lastActiveStory.split("/")
                            await initializeActiveStoryRef(userRef, doc(userRef, "stories", arr.pop())/*, state.app.portal*/)
                        }
                        initStoryRef();
                        let update = false;
                        if (currentUser.redirect) {
                            navigate(currentUser.redirect);
                            currentUser.redirect = null;
                        }
                        if (currentUser.authFunc) {
                            currentUser.authFunc();
                            currentUser.authFunc = null;
                        }
                        if (update) {
                            updateUser(currentUser);
                        }
                    } else if (state.app.portal.id) {
                        initializeFromPortal(state.app.portal.id, null, true)
                    }
                } else {
                    setupNewUser(currentUser)
                }
            })
        } catch (error) {
            console.log('Error subscribing to updates for user data: ', error)
        } finally {
            console.log("user init complete do user")
            //return () => unSubscribe();
        }
    }

    const initializeActiveStoryRef = async (userRef, storyRef/*, portal*/) => {
        let activeStoryRef = storyRef //doc(db, storyRef);
        /*if (storyRef) {
            // Check if story exists 
            await getDoc(doc(db, storyRef)).then(sDoc => {
                if(sDoc.exists()) activeStoryRef = sDoc
            });
        }*/
        // If not, check if there are other stories to set it to and set it to the first one
        if (!storyRef) {
            // The original activeStory no longer exists, so check fo any other stories
            const querySnapshot = await getDocs(query(collection(userRef, 'stories'), orderBy("date", "desc"), limit(1)));
            activeStoryRef = (querySnapshot.exists()) ? querySnapshot.docs[0].ref : null;
        }

        // If no stories, create one and set it
        if (!activeStoryRef) {
            await setupInitialStory(userRef)
                .then((newStoryRef) => activeStoryRef = newStoryRef)
                .catch((error) => {
                    console.log('Unable to initialize activeStoryRef. Creating new story failed.', error)
                })
        }
        if (activeStoryRef) {
            dispatch({ type: "UPDATE_ACTIVE_STORY_REF", payload: { activeStoryRef: activeStoryRef, setLastActive: true } });
            /*await setDoc(userRef, { "lastActiveStory": activeStoryRef.path }, {merge:true})
                .catch((error) => {
                    console.error('Error writing activeStoryRef to user document in database: ', error)
                })*/
        }
    }

    const setStoryOwner = () => {
        let userIsStoryOwner = false;

        const storyPathArray = state.stories.activeStoryRef.path.split("/");

        const userIndex = storyPathArray.findIndex((item) => item === "users") + 1;
        if (storyPathArray[userIndex] && currentUser?.uid === storyPathArray[userIndex]) {
            if (!state.app.userIsStoryOwner) dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner: true } });
        } else {
            if (state.app.userIsStoryOwner) dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner: false } });
        }
    }

    // USER DATA
    // Listen for updates to userData and initialize active story
    // If no user data, set up new user and new story
    useEffect(() => {
        const loc = window.location;  //  note:  useParams *should* work here, but doesn't, so we parse the URL for portal id
        // Check hunt path FIRST — it must be before /p/:id/:shotID because matchPath is
        // non-strict and /p/:id/:shotID also matches /p/:id/:shotID/hunt, swallowing hunt URLs.
        const huntPath = matchPath({
            path: '/p/:id/:shotID/hunt',
            exact: false,
            string: false
        }, loc.pathname)
        let isHunt = false;
        let path = null;
        if (huntPath) {
            path = huntPath;
            isHunt = true;
        }
        if (!path) {
            path = matchPath({
                path: '/p/:id/:shotID',
                exact: false,
                string: false
            }, loc.pathname)
        }
        if (!path) {
            path = matchPath({
                path: '/p/:id',
                exact: false,
                string: false
            }, loc.pathname)
        }
        if (isHunt) {
            dispatch({ type: "IS_HUNT", payload: true });
        }

          if (!path) {
            path = matchPath({
              path: '/p/:id/qr/:nth',
              exact: false,
              string: false
            }, loc.pathname)
          }

        let do_user_init = true
        let do_active_story_init = true
        let portal_id = "non-portal"
        let shot_id = null
        const handleGetPortal = async () => {
            const portal = await getPortal(portal_id);
            dispatch({ type: "UPDATE_PORTAL", payload: portal });
            return portal;
        }
        let cachedPortalData = null;
        if (!state.app.portal.id) {
            // A null id will get initialized to the portal id in the 
            console.log('PMM portal_id ATTEMPT ONE:', path)

            if (path != null && path?.params?.id != "undefined") {
                portal_id = path.params.id //(loc.pathname.match('/p/*')[0] === '/p/') ? loc.pathname.substring(3) : "non-portal"     
                // portal_id = (loc.pathname.match('/p/*')[0] === '/p/') ? loc.pathname.substring(3) : "non-portal"     

                do_user_init = false
                do_active_story_init = false
                handleGetPortal();
            } else {
                portal_id = "non-portal"
            }
        } else if (!Object.keys(state.userData).length) {
            portal_id = state.app.portal.id
            // do_user_init = false
            // If there's a logged-in user, always re-run initializeFromPortal so that
            // userIsStoryOwner is recalculated with the known uid (it may have been
            // set to false during anonymous load and never updated after login).
            do_active_story_init = currentUser ? true : (!Object.keys(state.stories.activeStory).length) ? true : false
            // Fix 2: Use already-fetched portal data from state to avoid re-fetching
            cachedPortalData = state.app.portal;
        } else {
            do_user_init = false
            do_active_story_init = false
            portal_id = state.app.portal.id
        }

        // Fix 1: Run doUserInit and initializeFromPortal in parallel when both are needed
        const promises = [];
        if (do_user_init) {
            promises.push(doUserInit(do_active_story_init));
        }
        if (do_active_story_init && portal_id) {
            promises.push(initializeFromPortal(portal_id, null, true, cachedPortalData));
        }
        if (promises.length) {
            Promise.all(promises);
        }

    }, [dispatch, currentUser, state.app.portal?.id/*, state.app.portal?.requirePass*/])


    // Make a new story out of next story
    // Next story is locally in the app.   It propagates out to the cloud and back. 
    useEffect(() => {
        if (!state.stories.nextStoryMetadata) return;
        if (state.stories.generateStory && state.userData.credits - state.stories.generateStory.cost < 0) return;
        const newStory = { ...state.stories.nextStoryMetadata, ...{ is_vertical: state.stories.generateStory?.is_vertical || false } };
        const userRef = doc(db, 'users', currentUser.uid);
        const newStoryId = nanoid(20);

        const createStory = async () => {
            await setDoc(doc(userRef, 'stories', newStoryId), newStory, { merge: true });
            const newStoryRef = doc(userRef, 'stories', newStoryId);
            const newShotRef = collection(userRef, 'stories', newStoryId, 'shots')
            if (newStory.shots.length) {
                newStory.shots.forEach(async (shot, key) => {
                    await setDoc(doc(newShotRef, shot.id), shot, { merge: true })
                })
            }
            // setDoc(userRef, {"lastActiveStory": newStoryRef.path}, {merge:true});
            setDoc(newStoryRef, newStory, { merge: true })
            const portalRef = doc(db, 'portals', newStory.portal_id);
            const newPortal = {
                creator_id: currentUser.uid,
                story_id: newStoryId,
                created_at: serverTimestamp(),
                deleted_at: null,
                requirePass: null
            }
            setDoc(portalRef, newPortal, { merge: true })
                .then(() => {
                    logEvent(analytics, 'NEW_STORY')
                    initializeActiveStoryRef(userRef, newStoryRef/*, state.app.portal*/).then(() => {
                        dispatch({ type: "CLEAR_NEXT_STORY_METADATA" });
                        if (state.stories.generateStory) generateCompose(newStoryRef)
                    })
                }).catch((error) => {
                    console.error('Error creating new story: ', error)
                })
        }
        createStory();
    }, [state.stories.nextStoryMetadata])

    // Toggle Star
    // Toggle Star
    useEffect(() => {
        const run = async () => {
            if (!currentUser || !state.app.toggleStoryStar) return;
            const userRef = doc(db, 'users', currentUser.uid);
            const { isStarred, portal_id } = state.app.toggleStoryStar;
            const starredPortalRef = doc(userRef, 'starred_portals', portal_id);
            if (!isStarred) {
                await setDoc(starredPortalRef, { portal_id: portal_id }, { merge: true })
                    .then(async () => {
                        // const starred = { ...state.stories.starredStories, ...{ portal_id: portal_id }}
                        // dispatch({ type: "SET_STARRED_STORIES", payload: { starredStories:starredBoards } });
                        let data;
                        if (state.stories.storyList && state.stories.storyList.find(story => story.portal_id === portal_id)) {
                            data = state.stories.storyList.find(story => story.portal_id === portal_id);
                        } else {
                            data = await initializeFromPortal(portal_id);
                        }
                        const starredBoards = [...state.stories.starredStories, ...[data]]
                        dispatch({ type: "SET_STARRED_STORIES", payload: { starredStories: starredBoards } });
                    })
                    .catch((error) => console.error("Error starring portal: ", error));
            } else {
                await deleteDoc(starredPortalRef).then(() => {
                    const starred = state.stories.starredStories.filter(board => board.portal_id !== portal_id); // Remove the story from the state
                    dispatch({ type: "SET_STARRED_STORIES", payload: { starredStories: starred } });
                })
                    .catch((error) => console.error("Error unstarring portal: ", error));
                // need to add starred dispatch - removed item
            }
        };
        run();
    }, [state.app.toggleStoryStar])

    // Fork Story
    useEffect(() => {
        if (!state.app.forkingStory) return;
        const forkIt = async () => {
            const { forkingStory, mediaOption, userRef, newStory, storyRef } = state.app.forkingStory;
            const storyId = nanoid()
            let story, shots, takes;
            if (forkingStory) {
                story = forkingStory;
                const shotSnapshot = await getDocs(query(collection(storyRef, 'shots'), where('deleted_at', '==', null)))
                const activeShotList = [];
                shotSnapshot.forEach((doc) => {
                    if (doc.exists()) {
                        var shot = doc.data();
                        shot.ref = doc.ref;
                        activeShotList.push(shot)
                    }
                })
                shots = activeShotList;
                const takeSnapshot = await getDocs(query(collection(storyRef, 'takes'), where('deleted_at', '==', null)));
                const activeTakeList = [];
                takeSnapshot.forEach(snapDoc => {
                    if (snapDoc.exists()) {
                        var take = snapDoc.data();
                        take.ref = snapDoc.ref;
                        activeTakeList.push(take)
                    }
                })
                takes = activeTakeList;
            } else {
                story = state.stories.activeStory;
                shots = state.stories.activeShotList;
                takes = state.stories.activeTakeList.concat(state.stories.activeRefList);
            }
            const portalId = newStory.portal_id;
            const portalRef = doc(db, 'portals', portalId)
            const forkBatch = writeBatch(db);
            forkBatch.set(portalRef, {
                creator_id: state.userData.ref.id,
                story_id: storyId,
                created_at: serverTimestamp(),
            }, { merge: true });

            const forkStory = async () => {
                //const uRef = getDoc(userRef);
                const newStoryRef = doc(userRef, 'stories', storyId)
                forkBatch.set(newStoryRef, newStory, { merge: true })
                forkBatch.set(newStoryRef, { 'ref': newStoryRef }, { merge: true })
                // dispatch({ type: "UPDATE_ACTIVE_STORY_REF", payload: { activeStoryRef: newStoryRef, setLastActive:true } });
                const shotPromises = story.parts.flatMap(part =>
                    part.order.map(async (shot_id) => {
                        const this_shot = shots.filter((shot) => shot.id == shot_id)[0]
                        if (!this_shot || this_shot.deleted_at) {
                            console.log(`Shot not copied (deleted or not found) for ID: ${shot_id}`);
                            return;
                        }
                        const newShotRef = doc(newStoryRef, 'shots', shot_id)
                        let image = this_shot.image
                        let last_take_stream_url = null

                        if (this_shot.last_take_im) {
                            image = this_shot.last_take_im
                        }
                        if (this_shot.selected_take_id) {
                            const this_take_list = takes.filter((take) => take.id == this_shot.selected_take_id);
                            const this_take = this_take_list[0];
                            if (this_take) {
                                image = this_take.thumbnail;
                            }
                        }

                        let prompt = this_shot.prompt || "";
                        let composition = this_shot.prompt_composition || null;
                        let style = this_shot.prompt_style || null;

                        if (this_shot.selected_take_id) {
                            const this_veneer = state.stories.activeVeneerLUT[this_shot.selected_take_id];
                            if (this_veneer) {
                                prompt = this_veneer.prompt;
                                composition = this_veneer.prompt_composition || composition;
                                style = this_veneer.prompt_style || style;
                            }
                        }

                        const newShot = {
                            id: shot_id, // the same shot id, but different story. could enable trackable rights and merging one day
                            header: this_shot.header,
                            image: image,
                            meta: "",
                            prompt: prompt,
                            description: this_shot.description,
                            last_take_id: (mediaOption === "No Media") ? null : this_shot.last_take_id || null,
                            selected_take_id: (mediaOption === "No Media") ? null : this_shot.selected_take_id || null,
                            last_take_im: (mediaOption === "No Media") ? null : this_shot.last_take_im || null,
                            last_take_stream_url: (mediaOption === "No Media") ? null : last_take_stream_url,
                            deleted_at: null,
                            prompt_composition: composition,
                            prompt_style: style
                        };
                        // here we explicity add the fields ... in the future consider itter over all items and set the 4 above to null.  
                        if ("tgt_stream_url" in this_shot) {
                            newShot["tgt_stream_url"] = this_shot.tgt_stream_url;
                        }
                        if ("tgt_dur_ms" in this_shot) {
                            newShot["tgt_dur_ms"] = this_shot.tgt_dur_ms;
                        }
                        setDoc(newShotRef, newShot, { merge: true });
                    })
                );

                await Promise.all(shotPromises);
                // Copy settings
                try {
                    const settingsDoc = await getDoc(doc(storyRef, 'settings', '0'));
                    if (settingsDoc.exists) {
                        const settingsData = settingsDoc.data();
                        const newSettingsRef = doc(newStoryRef, 'settings', '0');
                        forkBatch.set(newSettingsRef, settingsData); // Copy the settings into the new story
                        console.log("Settings copied successfully.");
                    } else {
                        console.log("No settings found to copy.");
                    }
                } catch (error) {
                    console.error("Error fetching settings: ", error);
                }
                // Copy audio
                try {
                    const audioDoc = await getDoc(doc(storyRef, 'audio', '0'));
                    if (audioDoc.exists()) {
                        const audioData = audioDoc.data();
                        const newAudioRef = doc(newStoryRef, 'audio', '0');
                        forkBatch.set(newAudioRef, audioData); // Copy the settings into the new story
                        console.log("Settings copied successfully.");
                    } else {
                        console.log("No audio doc found to copy.");
                    }
                } catch (error) {
                    console.error("Error fetching audio: ", error);
                }
                if (mediaOption === "All Media") {
                    for (let take of takes) {
                        // Fallback to empty string if undefined & set empty ""
                        if (take.deleted_at) {
                            console.log(`Take not copied (deleted) for ID: ${takeData.id}`);
                            return;
                        }
                        const newTake = {
                            video_path: take.video_path || "",
                            thumbnail: take.thumbnail || "",
                            id: take.id,
                            shot_id: take.shot_id || "",
                            uploaded_at: take.uploaded_at || "",
                            uploader_id: take.uploader_id || "",
                            proxyLUT: state.app.settings.proxyIsBlackAndWhite ? "gray_strong_contrast" : "none",
                            is_vertical: state.stories.activeStory.is_vertical || false,
                            deleted_at: null,
                        };
                        const newRefTake = doc(newStoryRef, 'takes', take.id);
                        forkBatch.set(newRefTake, newTake, { merge: true });
                        // Copy veneers
                        const veneer = state.stories.activeVeneerLUT[take.id];
                        if (veneer) {
                            const newVeneerRef = doc(newStoryRef, 'veneer', take.id);
                            const oldVeneerPath = veneer.ref.path.toString();
                            const newVeneer = {
                                ...veneer,
                                deleted_at: null,
                                forked_source: oldVeneerPath,
                                abort_reason: "forked"
                            };
                            forkBatch.set(newVeneerRef, newVeneer);
                        } else {
                            console.log(`No veneer found for take ID: ${take.id}`);
                        }
                    }
                } else if (mediaOption === "Selected Media") {
                    for (let shot of shots) {
                        const selectedTakes = takes.filter(take => take.id === (shot.selected_take_id || shot.last_take_id));
                        for (let take of selectedTakes) {
                            const newTake = {
                                video_path: take.video_path || "",
                                thumbnail: take.thumbnail || "",
                                id: take.id,
                                shot_id: take.shot_id || "",
                                uploaded_at: take.uploaded_at || "",
                                uploader_id: take.uploader_id || "",
                                proxyLUT: state.app.settings.proxyIsBlackAndWhite ? "gray_strong_contrast" : "none",
                                is_vertical: state.stories.activeStory.is_vertical || false,
                                deleted_at: null
                            };
                            const newRefTake = doc(newStoryRef, 'takes', take.id);
                            forkBatch.set(newRefTake, newTake, { merge: true });
                            // Copy veneers
                            const veneer = state.stories.activeVeneerLUT[take.id];
                            if (veneer) {
                                const newVeneerRef = doc(newStoryRef, 'veneer', take.id);
                                const oldVeneerPath = veneer.ref.path.toString();
                                const newVeneer = {
                                    ...veneer,
                                    deleted_at: null,
                                    forked_source: oldVeneerPath,
                                    abort_reason: "forked"
                                };
                                forkBatch.set(newVeneerRef, newVeneer);
                            } else {
                                console.log(`No veneer found for take ID: ${take.id}`);
                            }
                        }
                    }
                }
                await forkBatch.commit();

                const payload = {
                    activeStoryRef: newStoryRef,
                    setLastActive: true
                }
                dispatch({ type: "UPDATE_ACTIVE_STORY_REF", payload: payload });
                /*await setDoc(userRef, { "lastActiveStory": newStoryRef.path }, { merge:true }).then(() => {
                    const payload = {
                        activeStoryRef: newStoryRef, 
                        setLastActive: true
                    }
                    dispatch({ type: "UPDATE_ACTIVE_STORY_REF", payload: payload });
                })
                .catch((error) => {
                    console.error('Error writing activeStoryRef to user document in database: ', error)
                })  */
            }
            forkStory();
            /*const unsetFork = async () => {
                state.app.forkingStory = null;
                /*if (state.stories.activeStoryRef) {
                    return await setDoc(state.stories.activeStoryRef, {"fork_story": false}, {merge:true})
                }*
            }
            
            unsetFork()*/
        }
        if (state.app.forkingStory) forkIt();
    }, [state.app.forkingStory])

    // Init and Fork
    useEffect(() => {
        if (!state.app.initAndFork) return;
        if (state.app.initAndFork.fork) initializeFromPortal(state.app.initAndFork.portalId, state.app.initAndFork.fork, true);
        dispatch({ type: "CLEAR_INIT_AND_FORK", payload: {} });
    }, [state.app.initAndFork]);

    // Save Cut
    useEffect(() => {
        if (state.app.savingCut) {
            let in_offset_ms = [];
            let out_offset_ms = [];
            const storyRef = state.stories.activeStory.ref;
            const { do_tgt_cut, sequence, tgt_audio, mute_clip_audio } = state.app.savingCut;
            // settings for render
            const do_low_res = true;
            const do_high_res = true;
            if (sequence["trims"].length) {
                sequence["trims"].map((trim) => {
                    if (trim) {
                        in_offset_ms.push(trim[0]);
                        out_offset_ms.push(trim[1]);
                    } else {
                        in_offset_ms.push(0);
                        out_offset_ms.push(0);
                    }
                })
            }
            // makes a zip archive at every save  ... its a bit slow, but we can render an export button when its ready   ... or we could do a popup alert message, only after a desired export action?
            const do_export = true;
            if (do_tgt_cut) {
                if (do_low_res) {
                    console.log("LOW RES tgt RENDER video_filenames:", sequence["proxy"]);
                    const cut_request_tgt = {
                        save_type: "tgt",
                        high_quality: "False",
                        video_filenames: sequence["proxy"],
                        audio: tgt_audio, // todo: get from import_media if it has audio? place here if there is only one chapter with clips in order?  story.last_audio
                        version: null,
                        stream_path: null,
                        thumbnail: null,
                        is_vertical: state.stories.activeStory.is_vertical || false,
                        created_at: serverTimestamp(),
                        completed_at: null,
                        mute_clip_audio: mute_clip_audio,
                        tgt: { media: sequence["tgt"] },
                    };
                    const cut_id_tgt = uuidv4();
                    const cutRefTgt = doc(storyRef, "cuts", cut_id_tgt)
                    setDoc(cutRefTgt, cut_request_tgt, { merge: true })
                        .then(() => {
                            // placeholder for later functionality
                            dispatch({ type: "START_CUT", payload: {} });
                        })
                        .catch((error) => {
                            console.log("Error creating HQ cut request:", cut_request_tgt);
                        });
                }

                if (do_high_res) {
                    const cut_request_tgt = {
                        save_type: "tgt",
                        high_quality: "True",
                        video_filenames: sequence["files"],
                        audio: tgt_audio,
                        version: null,
                        stream_path: null,
                        thumbnail: null,
                        is_vertical: state.stories.activeStory.is_vertical || false,
                        created_at: serverTimestamp(),
                        completed_at: null,
                        mute_clip_audio: mute_clip_audio,
                        tgt: { media: sequence["tgt"] },
                    };
                    const cut_id_tgt = uuidv4();
                    const cutRefTgt = doc(storyRef, "cuts", cut_id_tgt)
                    setDoc(cutRefTgt, cut_request_tgt, { merge: true })
                        .then(() => {
                            // placeholder for later functionality
                            dispatch({ type: "START_CUT", payload: {} });
                            logEvent(analytics, 'SAVE_CUT')
                        })
                        .catch((error) => {
                            console.log("Error creating HQ cut request:", cut_request_tgt);
                        });

                    if (do_export) {
                        const export_request_tgt = {
                            video_filenames: sequence["files"],
                            audio: tgt_audio,
                            version: null,
                            dst_zip_url: null,
                            created_at: serverTimestamp(),
                            mute_clip_audio: mute_clip_audio,
                            tgt: { media: sequence["tgt"] },
                        };
                        const export_id_tgt = cut_id_tgt; // uuidv4()
                        const exportRefTgt = doc(storyRef, "exports", export_id_tgt)
                        setDoc(exportRefTgt, export_request_tgt, { merge: true })
                            .then(() => {
                                dispatch({ type: "START_EXPORT", payload: {} });
                            })
                            .catch((error) => {
                                console.log(
                                    "Error creating export request:",
                                    export_request_tgt
                                );
                            });
                        console.log("tried export_request_tgt:", export_request_tgt);
                    }
                }
            } else {
                const normal_audio = state.stories.activeStory.audio ? state.stories.activeStory.audio : "silent";

                // non tgt cut is a render
                if (do_low_res) {
                    console.log("LOW RES RENDER video_filenames:", sequence["proxy"]);
                    const cut_id = uuidv4();
                    const cut_request = {
                        save_type: "basic",
                        high_quality: "False",
                        video_filenames: sequence["proxy"],
                        audio: normal_audio,// todo:  do we want this to be "silent"?
                        version: null,
                        in_offset_ms: in_offset_ms,
                        out_offset_ms: out_offset_ms,
                        stream_path: null,
                        thumbnail: null,
                        is_vertical: state.stories.activeStory.is_vertical || false,
                        created_at: serverTimestamp(),
                        completed_at: null,
                        mute_clip_audio: mute_clip_audio,
                    };

                    const cutRef = doc(storyRef, "cuts", cut_id)
                    setDoc(cutRef, cut_request, { merge: true })
                        .then(() => {
                            // placeholder for later functionality
                            dispatch({ type: "START_CUT", payload: {} });
                        })
                        .catch((error) => {
                            console.log("Error creating cut request:", cut_request);
                        });
                }

                if (do_high_res) {
                    console.log("HIGH RES RENDER video_filenames:", sequence["files"]);
                    // note: handling of video_path is not addressed for tgt videos (e.g. tgt_stream_video exist in low res, not hgh)
                    //   could:  backfill low res mized with high, or propogate a link to a private high res file
                    const cut_request_hq = {
                        save_type: "basic",
                        high_quality: "True",
                        video_filenames: sequence["files"], // this is empty
                        in_offset_ms: in_offset_ms,
                        out_offset_ms: out_offset_ms,
                        audio: normal_audio,
                        version: null,
                        stream_path: null,
                        thumbnail: null,
                        is_vertical: state.stories.activeStory.is_vertical || false,
                        created_at: serverTimestamp(),
                        completed_at: null,
                        mute_clip_audio: mute_clip_audio,
                    };

                    const cut_id_hq = uuidv4();
                    const cutRefHq = doc(storyRef, "cuts", cut_id_hq)
                    setDoc(cutRefHq, cut_request_hq, { merge: true })
                        .then(() => {
                            // placeholder for later functionality
                            dispatch({ type: "START_CUT", payload: {} });
                            logEvent(analytics, 'SAVE_CUT')
                        })
                        .catch((error) => {
                            console.log("Error creating HQ cut request:", cut_request_hq);
                        });

                    if (do_export) {
                        const export_request = {
                            video_filenames: sequence["files"],
                            audio: normal_audio, // null  ... note:  we are allowing the audio from tgt to come out in export, even if trimming media is not complete ...
                            version: null,
                            in_offset_ms: in_offset_ms,
                            out_offset_ms: out_offset_ms,
                            dst_zip_url: null,
                            created_at: serverTimestamp(),
                            tgt: null,
                            mute_clip_audio: mute_clip_audio,
                        };
                        const export_id = cut_id_hq; // uuidv4()
                        const exportRefTgt = doc(storyRef, "exports", export_id)
                        setDoc(exportRefTgt, export_request, { merge: true })
                            .then(() => {
                                dispatch({ type: "START_EXPORT", payload: {} });
                            })
                            .catch((error) => {
                                console.log("Error creating export request:", export_request);
                            });
                        console.log("tried export_request (non_tgt):", export_request);
                    }
                }
            }

            dispatch({ type: "CLEAR_LAST_CUT", payload: {} });
            dispatch({ type: "CLEAR_SAVE_CUT", payload: {} });
            dispatch({ type: "CLEAR_LAST_EXPORT", payload: {} });
        }
    }, [state.app.savingCut])


    // propate last_cut to cloud.  ONLY used for clearing after a save. 
    useEffect(() => {
        const activeStoryRef = state.stories.activeStoryRef
        if (!activeStoryRef) return;
        // patch for onSnapshot subscription management so it does not impact active story
        if (window.location.href.indexOf(state.stories.activeStory.portal_id) == -1) return;
        const unsetLastCut = async () => {
            return await setDoc(activeStoryRef, { "last_cut": null }, { merge: true })
                .catch((error) => {
                    console.log('Error updating last_cut:', error)
                    return error
                })
        }
        if (state.app.savingCut && state.stories.activeStory.last_cut === null) {
            unsetLastCut()
        }
    }, [state.stories.activeStory.last_cut])

    // update parts onchange
    /*useEffect(() => {
        const activeStoryRef = state.stories.activeStoryRef
        if(!activeStoryRef) return;
        const updateParts = async () => {
            return await setDoc(activeStoryRef, {"parts": state.stories.activeStory.parts}, {merge:true})
            .catch((error) => {
                console.log('Error updating parts:', error)
                return error
                })
        }
        if (state.stories.activeStory.parts.length !== 0) {
            updateParts()
        }
    }, [state.stories.activeStory.parts])*/


    // propate last_export to cloud.  used for clearing after a save. 
    useEffect(() => {
        if (!state.stories.activeStoryRef) return;
        // patch for onSnapshot subscription management so it does not impact active story
        if (window.location.href.indexOf(state.stories.activeStory.portal_id) == -1) return;
        const activeStoryRef = state.stories.activeStoryRef
        const setStorysLastExport = async (last_export) => {
            return await setDoc(state.stories.activeStoryRef, { "last_export": last_export }, { merge: true })
                .catch((error) => {
                    console.log('Error updating last_export:', last_export)
                    return error
                })
        }
        if (state.stories.activeStory.last_export === null) {
            setStorysLastExport(state.stories.activeStory.last_export)
        }
    }, [state.stories.activeStory.last_export])


    // STORY LIST
    // Listen for updates to story list (based on currentUser)
    useEffect(() => {
        if (!currentUser) return
        const getStories = () => {
            let unSubscribe = () => { };
            try {
                const storiesRef = collection(doc(db, 'users', currentUser?.uid), 'stories')

                unSubscribe = onSnapshot(storiesRef, async querySnapshot => {
                    const storyList = [];
                    querySnapshot.forEach(async (doc) => {
                        if (doc.exists() && (!doc.get("deleted_at") || doc.get("deleted_at") == null)) {
                            const data = doc.data();
                            const story = { ref: doc.ref, ...data }
                            storyList.push(story)
                        }
                    })
                    dispatch({ type: "UPDATE_STORY_LIST", payload: { storyList } });
                });
            } catch (error) {
                console.log('Error subscribing to updates for story list: ', error);
            } finally {
                return () => unSubscribe();
            }
        }
        getStories();

        /*const getCredits = () => {
            let unSubscribe = () => { };
            try {
                const userRef = doc(db, 'users', currentUser.uid)

                unSubscribe = onSnapshot(userRef, async querySnapshot => {
                    if(!querySnapshot || state.updateCredits) return;
                    let data = {...querySnapshot.data(), ...{ref: userRef}}
                    dispatch({ type: "UPDATE_USER_DATA", payload: { userData: data } });
                });
            } catch (error) {
                console.log('Error subscribing to user: ', error);
            } finally {
                return () => unSubscribe();
            }
        }
        getCredits();*/
    }, [dispatch, currentUser])

    // // SHARED STORY LIST
    // // Listen for updates to shared story list (based on currentUser)
    // useEffect(() => {
    //     if (!currentUser) return
    //     let unSubscribe = () => { };
    //     try {
    //         const userRef = await setDoc(doc(db, 'users').doc(currentUser.uid);
    //         const storiesRef = db.collectionGroup('stories')
    //             .where("sharedWith", "array-contains", userRef)
    //             .where("deleted_at", "==", null);
    //         unSubscribe = storiesRef.onSnapshot(querySnapshot => {
    //             const storyList = [];
    //             querySnapshot.forEach((doc) => {
    //                 if (doc.exists) {
    //                     const data = doc.data();
    //                     const story = { ref: doc.ref, ...data }
    //                     storyList.push(story)
    //                 }
    //             })
    //             dispatch({ type: "UPDATE_SHARED_STORY_LIST", payload: { sharedStoryList: storyList } });
    //         })
    //     } catch (error) {
    //         console.log('Error subscribing to updates for shared story list: ', error)
    //     } finally {
    //         return () => unSubscribe();
    //     }


    // }, [dispatch, currentUser])

    // // ALBUM LIST
    // // Listen for updates to album list (based on currentUser)
    // useEffect(() => {
    //     if (currentUser) {
    //         let unSubscribe = () => { };
    //         try {
    //             const albumsRef = await setDoc(doc(db, 'albums')
    //                 .where("status", "==", "display")
    //                 .where("deleted_at", "==", null);
    //             unSubscribe = albumsRef.onSnapshot(querySnapshot => {
    //                 const albumList = [];
    //                 querySnapshot.forEach((doc) => {
    //                     if (doc.exists) {
    //                         const data = doc.data();
    //                         const album = { ref: doc.ref, ...data }
    //                         albumList.push(album)
    //                     }
    //                 })
    //                 dispatch({ type: "UPDATE_ALBUM_LIST", payload: { albumList } });
    //             })
    //         } catch (error) {
    //             console.log('Error subscribing to updates for album list: ', error)
    //         } finally {
    //             return () => unSubscribe();
    //         }


    //     }
    // }, [dispatch, currentUser])

    // ACTIVE STORY
    // Listen for updates to activeStory (based on state.activeStoryRef)
    useEffect(() => {
        console.log('PMM before ref is there to UPDATE_ACTIVE_STORY, portal id:  ', state.app.portal.id)

        if (!state.stories.activeStoryRef || !Object.keys(state.stories.activeStoryRef).length) return
        //console.log("active ref check", (state.stories.activeStoryRef && state.stories.activeStory && (state.stories.activeStoryRef.path.segments[state.stories.activeStoryRef.path.segments.length - 1] == state.stories.activeStory.ref[state.stories.activeStory.ref.path.segments.length - 1])))
        /*let unSubscribe = () => { };
        try {*/
        const activeStoryRef = state.stories.activeStoryRef
        /* unSubscribe =
             onSnapshot(doc(activeStoryRef.path), snapDoc => {*/

        const getActiveStory = async () => {
            if (activeStoryRef) {
                // Use the portal ID from state — NOT activeStoryRef.id which is the story doc ID,
                // not the portal ID. Passing the wrong ID here was causing checkPortalPass to
                // fetch portals/{storyId} (not found), dispatch UPDATE_PORTAL with {}, clear
                // state.app.portal.id, and trigger an infinite re-fetch loop.
                const portalIdForPassCheck = state.app.portal.id || null;
                const pass = portalIdForPassCheck ? await checkPortalPass(portalIdForPassCheck) : true;
                if (!pass) return;

                let unSubscribe = () => { };
                try {
                    /*var activeStory = await getDoc(db, activeStoryRef);
                    if(activeStory) activeStory = activeStory.data();
                    else return;*/
                    unSubscribe = onSnapshot(activeStoryRef, async querySnapshot => {
                        //let activeStory = await getDoc(querySnapshot);
                        //if(!activeStory) return
                        //activeStory = activeStory.data();
                        if (!querySnapshot) return;
                        let activeStory = querySnapshot.data()
                        // Guard: skip if story is already loaded. Compare doc ref IDs rather than
                        // portal_id fields which may be undefined, causing undefined===undefined=true
                        // and permanently blocking UPDATE_ACTIVE_STORY from firing.
                        if (!activeStory) return;
                        if (state.stories.activeStory?.ref?.id === activeStoryRef.id) return;
                        const imports = await getDocs(collection(activeStoryRef, 'imports'));
                        activeStory.autostory = imports.docs.length > 0 ? true : false;
                        // patch for onSnapshot subscription management so it does not impact active story
                        // Use portal id from state as fallback — activeStory.portal_id may be
                        // undefined on older documents, making indexOf(undefined) always -1.
                        const _portalIdForUrlCheck = activeStory.portal_id || state.app.portal.id;
                        if (_portalIdForUrlCheck && window.location.href.indexOf(_portalIdForUrlCheck) == -1) return;
                        activeStory.ref = activeStoryRef;
                        // for back compatibility < 0.3.21 to append an ID to each part if it does not have one
                        if (activeStory?.parts.length && !activeStory.parts[0].id) {
                            for (var i = 0; i < activeStory.parts.length; i++) {
                                if (!('id' in activeStory.parts[i])) {
                                    activeStory.parts[i] = { ...activeStory.parts[i], ...{ id: nanoid() } }
                                }
                            }
                        }
                        // check for deleted
                        if (activeStory.deleted_at) {
                            activeStory.isDeleted = true;
                        }
                        dispatch({ type: "UPDATE_ACTIVE_STORY", payload: { activeStory: activeStory } });
                        console.log('PMM UPDATE_ACTIVE_STORY dipatched ', activeStory)

                    })
                } catch (error) {
                    console.log('Error subscribing to updates for story list: ', error)
                } finally {
                    return () => unSubscribe();
                }
            } else {
                dispatch({ type: "CLEAR_ACTIVE_STORY_REF" });
            }
            //});
        }
        getActiveStory();
        /*} catch (error) {
            console.log('Error subscribing to updates for active story: ', error)
        } finally {
            return () => unSubscribe();
        }*/
    }, [state.stories.activeStoryRef, currentUser]);

    // update story style
    // update story style
    useEffect(() => {
        const run = async () => {
            if (!currentUser || !state.stories.activeStoryRef) return;
            if (state.stories.activeStoryRef && state.stories.activeStory.storyStyle && state.stories.activeStory.storyStyle !== "") {
                await setDoc(state.stories.activeStoryRef, { "storyStyle": state.stories.activeStory.storyStyle }, { merge: true })
                    .catch((error) => {
                        console.error("Error updating story style:", error);
                    });
            }
        };
        run();
    }, [state.stories.activeStory.storyStyle])

    // update credits
    useEffect(() => {
        if (!currentUser || !state.app.updateCredits || state.app.updateCredits == 0) return;
        const userRef = doc(db, 'users', currentUser.uid);
        // note:  we don't have a listener in the app, so potential future cloud billing might be wiped out by next user puchase

        //[test]
        // draft.userData.credits = draft.userData.credits + action.payload.credits
        setDoc(userRef, { "credits": increment(state.app.updateCredits), "updated_at": serverTimestamp() }, { merge: true })
        dispatch({ type: "UPDATE_USER_CREDITS", payload: { credits: 0 } });
    }, [state.app.updateCredits, currentUser])

    useEffect(() => {
        if (!currentUser || !state.app.createVeneer) return;
        const { veneer_id, veneer, cost, userData, story } = state.app.createVeneer;
        if (userData.credits && userData.credits < cost) {
            dispatch("UPDATE_USER_CREDITS", { credits: 0 });
            return;
        }
        const handleVeneer = async () => {
            const veneerRef = doc(story.ref, 'veneer', veneer_id)
            await setDoc(veneerRef, veneer, { merge: true });//.then(() => {
            dispatch({ type: "START_VENEER", payload: {} });
            dispatch({ type: "UPDATE_USER_CREDITS", payload: { credits: -cost } });
            if (story.isCompetition && (story.timerStart == undefined || story.timerStart == null)) {
                const timerStart = Date.now();
                const payload = { timerStart: timerStart };
                dispatch({ type: "START_STORY_TIMER", payload: payload });
            }
            const takePayload = {
                video_path: state.stories.activeStory?.is_vertical ? assets['processing_veneer_vert_path'] : assets['processing_veneer_thumb_path'],
                thumbnail: state.stories.activeStory?.is_vertical ? assets['processing_veneer_vert_gif'] : assets['processing_veneer_thumb_gif'], // assets['processing_veneer_thumb'],
                shot_id: veneer.shot_id,
                take_id: veneer_id,
                source: "generation",
                is_vertical: state.stories.activeStory?.is_vertical || false
            }
            dispatch({ type: "ADD_TAKE_TO_SHOT", payload: takePayload })
            const shotPayload = {
                take_id: veneer_id,
                // uploader_id: "veneer", // msg.input.user_id,   // we did this when it was in the cloud  ... now its a name, so autoveneer [disabled] might apply to veneer as well?
                // media_source: "veneer", // ToDo:  this for upload & capture too, also yt
                image: state.stories.activeStory?.is_vertical ? assets['processing_veneer_vert_jpg'] : assets['processing_veneer_thumb_jpg'],
                stream_path: state.stories.activeStory?.is_vertical ? assets['processing_veneer_vert_streampath'] : assets['processing_veneer_streampath'],
                shot_id: veneer.shot_id
            };
            dispatch({ type: "UPDATE_SHOT_LAST_TAKE", payload: shotPayload })
            state.app.userIsStoryOwner ? logEvent(analytics, 'OWNER_VENEER') : logEvent(analytics, 'COMMUNITY_VENEER')
            dispatch({ type: "CLEAR_CREATE_VENEER", payload: {} });
        }
        /*.catch((error) => {
            console.log("Error creating veneer request:", veneer);
        });*/
        handleVeneer();
    }, [state.app.createVeneer, currentUser]);


    // ACTIVE STORY SETTINGS
    // Listen for updates to activeStory (based on state.activeStoryRef)
    useEffect(() => {
        if (!state.stories.activeStory.ref) return
        let unSubscribeSettings = () => { };
        try {
            const settingsRef = doc(state.stories.activeStory.ref, 'settings', '0')
            let did_dispatch = false;
            unSubscribeSettings =
                onSnapshot(settingsRef, (snapDoc => {
                    if (snapDoc.exists()) {
                        const settings = snapDoc.data();
                        dispatch({ type: "UPDATE_STORY_SETTINGS", payload: { settings: settings } });
                        did_dispatch = true
                    } else {
                        if (currentUser) {
                            const initSettings = initialState.app.settings;
                            dispatch({ type: "UPDATE_STORY_SETTINGS", payload: { settings: initSettings } });
                            setDoc(settingsRef, initSettings, { merge: true })
                        }
                    }
                }));

        } catch (error) {
            const msg = error.message
            console.log('Error subscribing to updates for story settings: ', msg)
            if (msg.search("empty") > 1) {
                console.log('EMPTY DATA for story settings: ', msg)
            }
            else {
                console.log('ANOTHER ERROR for story settings: ', error)
            }
        } finally {
            return () => unSubscribeSettings();
        }
    }, [state.stories.activeStory.ref]);

    // SETTINGS
    // Update story settings
    useEffect(() => {
        if (!state.stories.activeStoryRef) return
        const updateSettings = async () => {
            try {
                const settingsRef = doc(state.stories.activeStoryRef, 'settings', '0')
                await setDoc(settingsRef, state.app.settings, { merge: true })
            } catch (error) {
                const msg = error.message
                console.log('Error updating story settings: ', msg)
            }
            dispatch({ type: "CLEAR_NEW_SETTINGS" });
        }
        updateSettings();

    }, [state.app.newSettings]);

    // PORTAL
    // Listen for requests to make a new portal
    useEffect(() => {

        if (!currentUser || !state.stories.activeStoryRef) {
            return
        }
        if (!state.app.portal.requested_id) {
            console.log("no portal requested ID")
            return
        }

        if (state.stories.activeStory.portal_id) {
            console.log("WARNING:   Portal already exists, don't make another", state.stories.activeStory.portal_id)
            return

            //ToDO later: enable user request for portal URL
            // Q: If there is an existing portal for this story, should we delete it?
            //
        }

        const portal_id = state.app.portal.requested_id
        const createPortal = async (portal_id) => {
            const portalRef = doc(db, 'portals', portal_id);
            const newPortal = {
                creator_id: currentUser.uid,
                story_id: state.stories.activeStoryRef.id,
                created_at: serverTimestamp(),
                deleted_at: null,
                requirePass: null
            }

            return await function () {
                const ref = setDoc(portalRef, newPortal, { merge: true });
                return ref;
            }.catch((error) => {
                console.log('Error creating new portal:', portal_id)
                return error
            })
        }

        const setStorysPortal = async (portal_id) => {
            return await function () {
                const story = setDoc(state.stories.activeStoryRef, { "portal_id": portal_id }, { merge: true });
                console.log("Story updated successfully.");
                return story;
            }
                .catch((error) => {
                    console.log('Error updating portal id:', portal_id)
                    return error
                })
        }

        // ASIDE:  it would be nice if these two actions were atomic
        // maybe serverside logic would help
        createPortal(portal_id)
        setStorysPortal(portal_id)
        dispatch({ type: "CLEAR_REQUESTED_PORTAL" });


        //await createPortal(portal_id)

    }, [state.app.portal.requested_id]);

    // Listen for updates to STORY (based on change in PORTAL ID)
    useEffect(() => {
        // const portal = state.app.portal
        // if (!portal.id) {
        //     // A null id will get initialized to the portal id in the URL
        //     let portal_id = "non-portal"
        //     if (loc.pathname.match('/p/*')){
        //         portal_id = (loc.pathname.match('/p/*')[0] === '/p/') ? loc.pathname.substring(3) : "non-portal"        
        //         dispatch({ type: "UPDATE_PORTAL", payload: { portal_id } });
        //     } else {
        //         console.log("NONMATCH PORTAL")
        //     }
        // }
        // if (portal.id) {
        //     console.log("EFFECT PORTAL_ID", portal.id)
        //     // this logic could move to initializeActiveStoryRef

        //     const portalRef =  await setDoc(doc(db, 'portals').doc(portal.id) 
        //     const _portal = portalRef.get().then((doc) => {
        //         if (doc.exists) {
        //             console.log("PORTAL data:", doc.data());
        //             const user_id = doc.data().creator_id
        //             const story_id = doc.data().story_id

        //             const activeStoryRefX =  await setDoc(doc(db, 'users').doc(user_id).collection('stories').doc(story_id) // db.database().ref('portal/' + portal_id + '/story_path');
        //             dispatch({ type: "UPDATE_ACTIVE_STORY_REF", payload: { activeStoryRefX } });

        //             let unSubscribe = () => { };
        //             //try {     
        //                     unSubscribe =
        //                     activeStoryRefX.onSnapshot(doc => {
        //                         if (doc.exists && !doc.get("deleted_at")) {
        //                             const activeStory = doc.data();
        //                             activeStory.ref = doc.ref;
        //                             dispatch({ type: "UPDATE_ACTIVE_STORY", payload: { activeStory } });
        //                         } else {
        //                             console.log("Story? No such document!");
        //                             //dispatch({ type: "CLEAR_ACTIVE_STORY_REF" });
        //                         }
        //                     });
        //             // catch (error) {
        //             //         console.log('Error querying data for portal defined Story: ', error)
        //             //     } finally {
        //             //         return () => unSubscribe();
        //             //     }
        //             // }

        //         } else {
        //             // doc.data() will be undefined in this case
        //             console.log("No such document!");
        //         }
        //     }).catch((error) => {
        //         console.log("Error getting document:", error);
        //     });
        // }
    }, [dispatch, state.app.portal])


    // LAST ACTIVE STORY
    // Set lastActiveStory for the user
    useEffect(() => {
        if (!currentUser || !state.stories.activeStory || !state.stories.activeStory.ref) return
        // patch for onSnapshot subscription management so it does not impact active story
        /*console.log(state.userData.lastActiveStory);
        console.log("portal:", state.stories.activeStory.portal_id)
        if(state.userData.lastActiveStory.indexOf(state.stories.activeStory.portal_id) != -1) return;
        const userRef = doc(db, 'users', currentUser.uid);
        const setLast = () => {
            setDoc(userRef, { "lastActiveStory": state.stories.activeStory.ref.path }, {merge:true})
            .then(() => {
                dispatch({ type: "SET_LAST_ACTIVE", payload: { portal_id: state.stories.activeStory.portal_id } })
            })
            .catch((error) => {
                console.log('Error setting lastActiveStory for user: ', userRef.id, error)
            })
        }*/

        if (state.stories.setLastActive && state.userData.lastActiveStory != state.stories.activeStory.ref.path) {
            const userRef = doc(db, 'users', currentUser.uid);
            const setLast = () => {
                setDoc(userRef, { "lastActiveStory": state.stories.activeStory.ref.path }, { merge: true })
                    .then(() => {
                        dispatch({ type: "SET_LAST_ACTIVE", payload: { portal_id: state.stories.activeStory.portal_id } })
                    })
                    .catch((error) => {
                        console.log('Error setting lastActiveStory for user: ', userRef.id, error)
                    })
            }
            setLast();
            dispatch({ type: "CLEAR_LAST_ACTIVE", payload: {} })
        } /*else if(state.stories.setLastActive && state.userData.lastActiveStory == state.stories.activeStory.ref.path) {
            dispatch({ type: "SET_LAST_ACTIVE", payload: { portal_id: state.stories.activeStory.portal_id } })
        }*/
    }, [dispatch, state.stories.activeStory, currentUser]);


    // useEffect(() => {
    //     if (!currentUser || !state.stories.activeStory || !state.stories.activeStory.ref) {
    //         dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner: false } });
    //         return;
    //     }

    //     let userIsStoryOwner = false;
    //     const storyPathArray = state.stories.activeStory.ref.path.split("/");
    //     const userIndex = storyPathArray.findIndex(item => item === "users") + 1;
    //     const ownerIdInPath = storyPathArray[userIndex];

    //     console.log("Current User ID:", currentUser.uid);
    //     console.log("Owner ID in Path:", ownerIdInPath);

    //     if (currentUser.uid === ownerIdInPath) {
    //         userIsStoryOwner = true;
    //     }

    //     console.log("Final Calculated userIsStoryOwner:", userIsStoryOwner);
    //     dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner } });
    // }, [currentUser, state.stories.activeStoryRef, state.stories.activeStory]);


    useEffect(() => {
        if (currentUser && currentUser.uid == state.app.portal?.creator_id) {
            //setStoryOwner();
            dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner: true } });
        } else if (state.app.portal?.creator_id) {
            dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner: false } });
        }
    }, [currentUser, state.app.portal]);



    // // ACTIVE CARD LIST is similar to ACTIVE SHOT LIST ... see below
    // // Listen for changes to activeShotList
    // useEffect(() => {
    //     if (!currentUser || !state.stories.activeStoryRef) return
    //     let unSubscribe = () => { };
    //     try {
    //         const activeStoryRef = state.stories.activeStoryRef
    //         const shotsRef = activeStoryRef
    //             .collection('cards')
    //             .where('deleted_at', '==', null)
    //             .where('display', '==', true)
    //             .orderBy('orientation', 'desc')
    //             .orderBy('created_at', 'asc')
    //         unSubscribe = shotsRef.onSnapshot(querySnapshot => {
    //             const activeShotList = [];
    //             const sourceSet = [];
    //             querySnapshot.forEach((doc) => {
    //                 if (doc.exists) {
    //                     const card = doc.data();
    //                     card.ref = doc.ref;
    //                     activeShotList.push(card)
    //                     if (card.image_source_id) {
    //                         sourceSet.push(card.image_source_id)
    //                     }
    //                 }
    //             })
    //             dispatch({ type: "UPDATE_ACTIVE_SHOT_LIST", payload: { activeShotList } });
    //             dispatch({ type: "UPDATE_ALBUM_SOURCE_SET", payload: { sourceSet } });
    //             dispatch({ type: "APP_LOADED", payload: {} });
    //         })
    //     }
    //     catch (error) {
    //         console.log('Error querying data for activeShotList: ', error)
    //     } finally {
    //         return () => unSubscribe();
    //     }
    // }, [dispatch, state.stories.activeStoryRef, currentUser]);

    // ACTIVE SHOT LIST
    useEffect(() => {
        if (!state.stories.activeStoryRef) return
        const activeStoryRef = state.stories.activeStoryRef
        if (!activeStoryRef) return

        // Fix 5: Collect all unsubscribe functions for proper cleanup
        const unsubscribes = [];

        // Shots listener
        try {
            const shotRef = query(collection(activeStoryRef, 'shots'), where('deleted_at', '==', null))
            const unsubShots = onSnapshot(shotRef, querySnapshot => {
                const activeShotList = [];
                querySnapshot.forEach((doc) => {
                    if (doc.exists()) {
                        var shot = doc.data();
                        shot.ref = doc.ref;
                        activeShotList.push(shot)
                    }
                })
                dispatch({ type: "UPDATE_ACTIVE_SHOT_LIST", payload: { activeShotList } });
                dispatch({ type: "APP_LOADED", payload: {} });
            });
            unsubscribes.push(unsubShots);
        }
        catch (error) {
            console.log('Error querying data for activeShotList: ', error)
        }

        // Takes listener
        try {
            const takesRef = query(collection(activeStoryRef, 'takes'), where('deleted_at', '==', null))
            const unsubTakes = onSnapshot(takesRef, querySnapshot => {
                const activeTakeList = [];
                const activeRefList = [];
                querySnapshot.forEach(snapDoc => {
                    if (snapDoc.exists()) {
                        var take = snapDoc.data();
                        take.ref = snapDoc.ref;
                        if (take.is_ref) activeRefList.push(take);
                        if (take.shot_id && take.shot_id !== "reference") activeTakeList.push(take)
                    }
                })
                dispatch({ type: "UPDATE_ACTIVE_TAKE_LIST", payload: { activeTakeList } });
                dispatch({ type: "UPDATE_ACTIVE_REF_LIST", payload: { activeRefList } });
            });
            unsubscribes.push(unsubTakes);
        }
        catch (error) {
            console.log('Error querying data for activeTakeList: ', error)
        }

        // User state (one-time fetch, not a listener)
        const getUserState = async () => {
            try {
                if (!activeStoryRef || !currentUser) return
                console.log("getUserState", state.generate, currentUser.uid)
                await getDoc(doc(activeStoryRef, "user_state", currentUser.uid)).then((doc) => {
                    if (doc.exists()) {
                        const data = doc.data();
                        if (data.hasOwnProperty("userRefs")) {
                            dispatch({ type: "UPDATE_USER_REFS", payload: data.userRefs });
                        }
                        if (data.hasOwnProperty("prompt")) {
                            dispatch({ type: "UPDATE_PROMPT", payload: data.prompt });
                        }
                        if (data.hasOwnProperty("model")) {
                            dispatch({ type: "UPDATE_SELECTED_MODEL", payload: data.model });
                        }
                        if (data.hasOwnProperty("method")) {
                            dispatch({ type: "UPDATE_SELECTED_METHOD", payload: data.method });
                        }
                        if (data.hasOwnProperty("output")) {
                            dispatch({ type: "UPDATE_SELECTED_OUTPUT", payload: data.output });
                        }
                    }
                });
            } catch (error) {
                console.log('Error querying data for userRefs: ', error)
            }
        };
        getUserState();

        // Audio (one-time fetch)
        const getAudio = async () => {
            try {
                await getDoc(doc(activeStoryRef, "audio", "0")).then((doc) => {
                    if (doc.exists()) dispatch({ type: "UPDATE_AUDIO", payload: doc.data() });
                    else dispatch({ type: "UPDATE_AUDIO", payload: {} });
                })
            } catch (error) {
                console.log('Error querying data for audio: ', error)
            }
        };
        getAudio();

        // Fix 5: Return composed cleanup that unsubscribes all listeners
        return () => {
            unsubscribes.forEach(unsub => unsub());
        };

    }, [dispatch, state.stories.activeStoryRef]);

    useEffect(() => {

    }, [state.stories.activeStory.audio]);

    useEffect(() => {
        // migrate audio from last_audio to audio[0]
        if (state.stories.activeStory.last_audio && !state.app.migratingAudio) dispatch({ type: "MIGRATE_AUDIO", payload: {} });
    }, [state.stories.activeStory.last_audio])

    useEffect(() => {
        const run = async () => {
            if (!state.app.migratingAudio || !state.stories.activeStory) return;
            const storyRef = state.stories.activeStoryRef;
            const audioDocRef = doc(storyRef, "audio", "0");
            const activeStory = state.stories.activeStory;
            const index = activeStory.last_audio.lastIndexOf("/");
            const audioDoc = {
                audio_path: activeStory.last_audio,
                file_name: activeStory.audio_file_name ? activeStory.audio_file_name : activeStory.last_audio.substring(index + 1),
                uploaded_at: serverTimestamp(),
                uploader_id: currentUser.uid,
            };
            setDoc(audioDocRef, audioDoc).then(() => {
                if (activeStory.audio_file_name) setDoc(storyRef, { last_audio: null, audio_file_name: null }, { merge: true })
                setDoc(storyRef, { last_audio: null }, { merge: true }).then(() => {
                    dispatch({ type: "END_MIGRATING_AUDIO", payload: {} })
                })
            })
        };
        run();
    }, [state.app.migratingAudio])




    // // NUM SHOTS  -  just use shots.length 
    // // Listen for changes to activeStory
    // useEffect(() => {
    //     if (!state.stories.activeStory) return
    //     if (!state.stories.activeShotList) return

    //     // // it seems we should count up the length of the lists in ORDER.   but the fields dont exist yet!  maybe activeStory is a promose?
    //     // if ("parts" in state.stories.activeStory && "order" in state.stories.activeStory.parts) {
    //     //     const num_shots = state.stories.activeStory.parts.reduce((count, part) => count = count + part.order.length, 0) 
    //     // } else {
    //     //     console.log("WARNING:  shot count is FAILING!", state.stories.activeStory)
    //     // }

    //     const num_shots = state.stories.activeShotList.length   // instead we do this, which SHOULD work beacuse deleted_at is filetered out
    //     dispatch({ type: "UPDATE_NUM_SHOTS", payload: { num_shots: num_shots } });              


    // }, [dispatch, state.stories.activeShotList]);  //  state.stories.activeStory, 


    // ACTIVE TAKE LIST
    // Listen for changes to activeTakeList
    /* useEffect(() => {
        //if (!currentUser || !state.stories.activeStoryRef) return  // user dependency removed.
        if (!state.stories.activeStory) return
        if (!state.stories.activeStoryRef) return
        let unSubscribe = () => { };
        try {
            //console.log("try")
            const activeStoryRef = state.stories.activeStoryRef
            const storyRef = query(collection(activeStoryRef, 'takes'), where('deleted_at', '==', null))
            unSubscribe = onSnapshot(storyRef, querySnapshot => {
                const activeTakeList = [];
                querySnapshot.forEach(snapDoc => {

                    if (snapDoc.exists()) {
                        var take = snapDoc.data();
                        take.ref = snapDoc.ref;
                        activeTakeList.push(take)
                    }
                })
                dispatch({ type: "UPDATE_ACTIVE_TAKE_LIST", payload: { activeTakeList } });
            })
        }
        catch (error) {
            console.log('Error querying data for activeTakeList: ', error)
        } finally {
            return () => unSubscribe();
        }
    }, [state.stories.activeStoryRef]); */

    // ACTIVE VENEER LIST
    // Listen for changes to activeVeneerList, and update the LUT
    useEffect(() => {
        /*
        Merge Conflict
        if (!state.stories.activeStory || !state.stories.activeStoryRef) {
            if (state.stories.activeStoryRef && !state.stories.activeStory && state.stories.storyList) {
                const data = state.stories.activeStoryRef.data();
                if (state.stories.storyList.find(story => story.portal_id === data.portal_id)) {
                    const activeStory = state.stories.storyList.find(story => story.portal_id === data.portal_id)
                    dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner: currentUser.uid == activeStory.creator_id } });
                    dispatch({ type: "SET_ACTIVE_STORY", payload: { activeStory: activeStory } });
                } else {
                    initializeFromPortal(data.portal_id)
                }
            }
            return
        }*/
        if (!state.stories.activeStoryRef) return
        console.log("update current refs effect")
        let unSubscribe = () => { };
        try {
            const veneerRef = collection(state.stories.activeStoryRef, 'veneer')
            unSubscribe = onSnapshot(veneerRef, async querySnapshot => {
                const activeVeneerList = [];
                if (querySnapshot.empty) { // migrate veneers
                    const createdAt = new Date(state.stories.activeStory?.created_at).getTime();
                    const nov = new Date(2024, 11, 1).getTime();
                    const mar = new Date(2025, 3, 24).getTime();
                    if (createdAt > nov && createdAt < mar) {
                        const veneersRef = collection(state.stories.activeStoryRef, 'veneers')
                        await getDocs(veneersRef, querySnapshot => {
                            if (querySnapshot.empty) return;
                            const batch = writeBatch(db);
                            querySnapshot.forEach((qDoc) => {
                                if (qDoc.exists()) {
                                    var vn = qDoc.data();
                                    if (vn.deleted_at) return;
                                    vn.ref = qDoc.ref;
                                    activeVeneerList.push(vn)
                                    const vDoc = doc(veneerRef, qDoc.id);
                                    batch.set(vDoc, vn, { merge: true });
                                }
                            })
                            // Commit the batch
                            batch.commit();
                        })
                    }
                } else {
                    querySnapshot.forEach((qDoc) => {
                        if (qDoc.exists()) {
                            var vn = qDoc.data();
                            if (vn.deleted_at) return;
                            vn.ref = qDoc.ref;
                            activeVeneerList.push(vn)
                        }
                    })
                }
                dispatch({ type: "UPDATE_ACTIVE_VENEER_LUT", payload: { activeVeneerList: activeVeneerList } });
            })
        }
        catch (error) {
            console.log('Error querying data for activeVeneerList: ', error)
        }
        // Fix 5: Proper cleanup — unsubscribe on dependency change
        return () => { unSubscribe(); };
    }, [state.stories.activeStoryRef]);

    // Current Refs
    /*useEffect(() => {
        if (!state.stories.activeStory || !state.stories.activeStoryRef) return
        // console.log("UPDATE_CURRENT_REFS effect", state.stories.activeStory, state.stories.activeStory?.currentRefs)
        // dispatch({ type: "UPDATE_CURRENT_REFS", payload: state.stories.activeStory.currentRefs || [] });
    }, [state.stories.activeStory, state.stories.activeStoryRef]);*/


    useEffect(() => {
        let prioritizeLivePreview = true
        if (!state.app.userIsStoryOwner) {
            prioritizeLivePreview = !state.app.settings.communityCanViewSavedPreview
        }
        dispatch({ type: "UPDATE_PRIORITIZE_LIVE_PREVIEW", payload: { prioritizeLivePreview: prioritizeLivePreview } });
    }, [state.app.userIsStoryOwner, state.app.settings.communityCanViewSavedPreview]);

    useEffect(() => {
        if (!state.stories.generateStory) return
        if (state.stories.generateStory.part_id) {
            if (state.stories.generateStory && state.userData.credits - state.stories.generateStory.cost < 0) return;
            const userRef = doc(db, 'users', currentUser.uid);
            const storyRef = state.stories.storyList.find(story => story.portal_id === state.stories.generateStory.portal_id)?.ref
            generateCompose(storyRef)
        } else {
            const story = emptyStory(state.stories.generateStory.portal_id, false, true);
            dispatch({ type: "NEW_ACTIVE_STORY", payload: { newStory: story } });
        }
    }, [state.stories.generateStory]);

    useEffect(() => {
        const run = async () => {
            if (!state.app.newRefTake || !state.stories.activeStoryRef) return;
            const activeStoryRef = state.stories.activeStoryRef
            const newTake = { ...state.app.newRefTake.take, ...{ "is_ref": true, "uploaded_at": serverTimestamp(), "uploader_id": currentUser.uid, "deleted_at": null } };
            const refList = state.stories.activeRefList || []
            if (state.app.newRefTake.addUserRef) {
                const userRefs = state.stories.activeStory?.userRefs.find(obj => obj.hasOwnProperty(currentUser?.uid))?.[currentUser.uid] || []
                const newUserRefs = [...userRefs, { id: newTake.id, type: "ref", active: false }]
                dispatch({ type: "UPDATE_USER_STATE", payload: { userRefs: newUserRefs } });
            }
            let takeRef = doc(activeStoryRef, 'takes', newTake.id);
            await setDoc(takeRef, newTake, { merge: true })

            const activeRefList = refList.find(ref => ref.id === newTake.id) ? refList : [...refList, newTake];
            dispatch({ type: "UPDATE_ACTIVE_REF_LIST", payload: activeRefList });
            dispatch({ type: "CLEAR_NEW_REF_TAKE", payload: {} });
        };
        run();
    }, [state.app.newRefTake, state.stories.activeStoryRef]);

    useEffect(() => {
        const run = async () => {
            if (!state.app.updateUserState || !currentUser || !state.stories.activeStoryRef) return
            const activeStoryRef = state.stories.activeStoryRef
            // change to userRefs/uid
            console.log("updateUserState", state.app.updateUserState)
            const stateRef = doc(activeStoryRef, "user_state", currentUser.uid)
            await setDoc(stateRef, state.app.updateUserState, { merge: true })
            if (state.app.updateUserState.hasOwnProperty("userRefs")) {
                dispatch({ type: "UPDATE_USER_REFS", payload: state.app.updateUserState.userRefs || [] });
            }
            if (state.app.updateUserState.hasOwnProperty("method")) {
                dispatch({ type: "UPDATE_SELECTED_METHOD", payload: state.app.updateUserState.method });
                /*if (!state.app.updateUserState.model) {
                    dispatch({ type: "UPDATE_SELECTED_MODEL", payload: null });
                }*/
            }
            if (state.app.updateUserState.hasOwnProperty("model")) {
                dispatch({ type: "UPDATE_SELECTED_MODEL", payload: state.app.updateUserState.model });
            }
            if (state.app.updateUserState.hasOwnProperty("prompt")) {
                dispatch({ type: "UPDATE_PROMPT", payload: state.app.updateUserState.prompt });
            }
            dispatch({ type: "CLEAR_UPDATE_USER_STATE", payload: {} });
        };
        run();
    }, [state.app.updateUserState])

    useEffect(() => {
        if (!state.app.storyImport || !Object.keys(state.app.storyImport).length) return;
        if (!currentUser) return draft;
        const importPayload = state.app.storyImport; //draft.stories.activeStory.ref //action.payload.storyRef;
        // let storyId = null;
        const bw = state.app.settings.proxyIsBlackAndWhite;
        const getStoryData = async () => {
            const portalRef = doc(db, 'portals', importPayload.portalId)
            return await getDoc(portalRef).then(async (pRef) => {
                if (pRef.exists()) {
                    //console.log("PORTAL data:", doc.data());  //defeats security if we just log it out, lol
                    const data = pRef.data();
                    //const user_id = data.creator_id
                    dispatch({ type: "UPDATE_PORTAL", payload: data });
                    return { story_id: data.story_id, creator_id: data.creator_id }

                }
            });
        }

        const handleStory = async () => {
            const storyData = /*(draft.stories.activeStory?.portal_id == action.payload.portalId) ? { story_id: draft.stories.activeStory.story_id, creator_id: draft.stories.activeStory.creator_id } : */await getStoryData();
            const story_id = storyData.story_id;
            const creator_id = storyData.creator_id;
            // console.log("HANDLE STORY ", story_id, action.payload.portalId, draft.stories.activeStory?.portal_id)
            const importStoryRef = doc(db, 'users', creator_id, 'stories', story_id);
            const uploader_id = (importPayload.currentUser) ? importPayload.currentUser.uid : "anon"
            const importData = {
                video_path: importPayload.video_path,
                thumbnail: importPayload.thumbnail,
                id: importPayload.id,
                type: "storyboard",
                uploaded_at: serverTimestamp(),
                //modified_at: Date.now(), 
                uploader_id: uploader_id,
                proxyLUT: bw ? "gray_strong_contrast" : "none",
                deleted_at: null
            };
            const importRef = doc(importStoryRef, 'imports', nanoid()) // importPayload.id)
            setDoc(importRef, importData, { merge: true })
                .then(() => {
                    vidRef;
                    if (currentUser.uid == storyData.creator_id) dispatch({ type: "SET_USER_IS_STORY_OWNER", payload: { userIsStoryOwner: true } });
                    logEvent(analytics, 'NEW_AUTOSTORY')
                })
                .catch((error) => {
                    console.log('Error importing scenes:', importRef)
                })
            //console.log("ADD_TAKE_TO_SHOT  cloud ", payload.take_id)
            dispatch({ type: "STORY_IMPORT", payload: {} });
        }


        /*if(!importStoryRef && action.payload.portalId) {
            getStoryRef();
        } else {
            handleStory();
        }*/
        handleStory();
    }, [state.app.storyImport]);


    // // ACTIVE ALBUM
    // // Fetch data for activeAlbum (based on state.activeAlbumRef)
    // useEffect(() => {

    //     const setupDefaultAlbum = async () => {
    //         const albumRef = await setDoc(doc(db, 'albums').doc();
    //         const newAlbum = albumConstructor();
    //         return albumRef.set(newAlbum)
    //             .then(() => albumRef)
    //             .catch((error) => {
    //                 console.log('Error creating default album:')
    //                 return error
    //             })
    //     }

    //     const initializeActiveAlbumRef = async () => {
    //         let activeAlbumRef = ""
    //         if (state.inspiration.initialAlbumID) {
    //             activeAlbumRef = await setDoc(doc(db, 'albums').doc(state.inspiration.initialAlbumID);
    //             // Check if album exists 
    //             activeAlbumRef = await activeAlbumRef.get().then((doc) => {
    //                 if (doc.exists && !doc.get("deleted_at")) {
    //                     return activeAlbumRef
    //                 } else {
    //                     return null;
    //                 }
    //             })
    //         }

    //         // If the album does not exist, check if there are other albums to set it to and set it to the first one
    //         if (!activeAlbumRef) {
    //             // The original activeAlbum no longer exists, so check fo any other albums
    //             activeAlbumRef = await await setDoc(doc(db, 'albums')
    //                 .where("status", "==", "display")
    //                 .where("deleted_at", "==", null)
    //                 .limit(1)
    //                 .get().then((querySnapshot) => {
    //                     if (!querySnapshot.empty) {
    //                         return querySnapshot.docs[0].ref
    //                     } else {
    //                         return null
    //                     }
    //                 })
    //         }

    //         // If still no albums, create one and set it
    //         if (!activeAlbumRef) {
    //             activeAlbumRef = await setupDefaultAlbum()
    //                 .then((newAlbumRef) => newAlbumRef)
    //                 .catch((error) => {
    //                     console.log('Unable to initialize activeAlbumRef. Creating new album failed.', error)
    //                 })
    //         }

    //         if (activeAlbumRef) {
    //             dispatch({ type: "UPDATE_ACTIVE_ALBUM_REF", payload: { activeAlbumRef } });
    //         }

    //     }


    //     if (!currentUser) return
    //     if (!state.inspiration.activeAlbumRef) {
    //         try {
    //             initializeActiveAlbumRef();
    //         } catch (error) {
    //             console.log('Error initializing active album: ', error)
    //         }
    //         return
    //     }

    //     let unSubscribe = () => { };
    //     try {
    //         const activeAlbumRef = state.inspiration.activeAlbumRef
    //         unSubscribe = activeAlbumRef.onSnapshot(doc => {
    //             if (doc.exists) {
    //                 const activeAlbum = doc.data();
    //                 if (!activeAlbum.deleted_at) {
    //                     activeAlbum.ref = doc.ref;
    //                     dispatch({ type: "UPDATE_ACTIVE_ALBUM", payload: { activeAlbum } });
    //                 }
    //             }
    //         });
    //     } catch (error) {
    //         console.log('Error subscribing to active album updates: ', error)
    //     } finally {
    //         return () => unSubscribe();
    //     }
    // }, [dispatch, state.inspiration.activeAlbumRef, state.inspiration.initialAlbumID, currentUser]);

    // // ACTIVE ALBUM SHOT LIST
    // // Listen for changes to activeAlbumCardList
    // useEffect(() => {
    //     if (!currentUser || !state.inspiration.activeAlbumRef) return
    //     const activeAlbumRef = state.inspiration.activeAlbumRef
    //     // const activeAlbumRef = await setDoc(doc(db, 'albums').doc(state.inspiration.activeAlbumRef.id)
    //     // if (typeof activeAlbumRef.collection !== 'function') return
    //     let unSubscribe = () => { };
    //     try {
    //         const shotsRef = activeAlbumRef
    //             .collection('cards')
    //             .where('deleted_at', '==', null)
    //             .where('display', '==', true)
    //             .orderBy('orientation', 'desc')
    //             .orderBy('created_at', 'asc')
    //         unSubscribe = shotsRef.onSnapshot(querySnapshot => {
    //             const cardList = [];
    //             querySnapshot.forEach((doc) => {
    //                 if (doc.exists) {
    //                     const card = doc.data();
    //                     card.ref = doc.ref;
    //                     cardList.push(card)
    //                 }
    //             })
    //             dispatch({ type: "UPDATE_ACTIVE_ALBUM_SHOT_LIST", payload: { activeAlbumCardList: cardList } });
    //         })
    //     }
    //     catch (error) {
    //         console.log('Error querying data for activeAlbumCardList: ', error)
    //     } finally {
    //         return () => unSubscribe();
    //     }
    // }, [currentUser, dispatch, state.inspiration.activeAlbumRef]);

    // TODO: preload images for story and inspiration? 


    return (
        <AppContext.Provider value={{ state, dispatch }}>
            {children}
        </AppContext.Provider>
    );
}

export default AppContext;